import { Track } from "../audio/Track.js";
import { Timeline } from "../timeline/Timeline.js";
import { ViewportRenderer } from "../rendering/ViewportRenderer.js";
import { LocalWaveform } from "../waveform/WaveformGenerator.js";
import { RemoteWaveform } from "../waveform/RemoteWaveform.js";
import { RemoteSpectrogram } from "../spectrogram/RemoteSpectrogram.js";
import { extractOriginalSampleRate } from "../audio/AudioFileUtils.js";
import { BufferTrackSource, StreamingTrackSource } from "../audio/TrackSource.js";
import { remoteFetch } from "./remoteFetch.js";
import { DbScale } from "../rendering/scales/DbScale.js";
import {
  frequencyToFraction,
  fractionToFrequency,
} from "../spectrogram/frequencyMapping.js";
import {
  interpolatorCatalogue,
  getInterpolator,
  DEFAULT_INTERPOLATOR_ID,
} from "../audio/interpolators/index.js";

/**
 * Soft-clip curve for the always-on per-track output limiter.
 *
 * A DynamicsCompressor's ratio is finite, so however hard gain drives it, the
 * output keeps climbing — just more slowly. This curve is a genuine asymptote
 * instead: linear (y = x, bit-transparent) below LIMITER_KNEE, then a tanh
 * knee that saturates smoothly toward LIMITER_CEILING as x → 1 but can never
 * mathematically cross it. Below the knee is where ordinary gain-staged audio
 * lives, so normal tracks at normal gain are untouched; only content pushed
 * toward full scale — by gain, by the optional compressor's makeup gain, or
 * both — gets crushed. A plain tanh(k·x) with no linear region would instead
 * apply gain (its slope at 0) or coloration to everything, including quiet
 * signals that were never in danger of clipping.
 *
 * Anything already past ±1 when it reaches the curve (what pushing gain well
 * past 0dB does to a sample) lands on that same LIMITER_CEILING value per the
 * WaveShaperNode spec, so it stays capped rather than climbing further.
 */
const LIMITER_KNEE = 0.8; // below this, y = x exactly
const LIMITER_CEILING = LIMITER_KNEE + (1 - LIMITER_KNEE) * Math.tanh(1); // ≈ 0.952
const LIMITER_CURVE_SIZE = 1024;
const LIMITER_CURVE = (() => {
  const curve = new Float32Array(LIMITER_CURVE_SIZE);
  for (let i = 0; i < LIMITER_CURVE_SIZE; i++) {
    const x = (i / (LIMITER_CURVE_SIZE - 1)) * 2 - 1; // [-1, 1]
    const mag = Math.abs(x);
    const shaped =
      mag < LIMITER_KNEE
        ? mag
        : LIMITER_KNEE + (1 - LIMITER_KNEE) * Math.tanh((mag - LIMITER_KNEE) / (1 - LIMITER_KNEE));
    curve[i] = Math.sign(x) * shaped;
  }
  return curve;
})();

export class AudioDAW {
  constructor(canvasElement, options = {}) {
    this.canvas = canvasElement;
    this.minimapCanvas = options.minimapCanvas || null;

    // Audio engine
    this.audioContext = null;
    this.isPlaying = false;
    this.currentTime = 0;
    this.transportAnchorTime = 0;
    this.transportAnchorContextTime = 0;
    this.animationFrameId = null;
    this.playbackSpeed = 1;
    this.interpolatorId = options.interpolator || DEFAULT_INTERPOLATOR_ID;
    /** WSOLA pitch preservation — proof of concept, see setPitchPreservation. */
    this.preservePitch = options.preservePitch || false;
    /** Speeds between which pitch is held exactly; beyond it, partially. */
    this.pitchRange = options.pitchRange || { min: 0.5, max: 2 };
    /**
     * Request options for every call to the analysis backend — auth, mostly.
     *
     * A `RequestInit`, or a function returning one (optionally async). The
     * function form is what survives a session: a token captured once expires,
     * and the symptom is a track that loads and then silently stops refining.
     */
    this.fetchOptions = options.fetchOptions || null;

    /**
     * Keep the transport running across a track change.
     *
     * Off, the transport stops the moment the timeline is empty — which is what
     * a swap looks like for the instant between removing one track and adding
     * the next. On, an empty timeline is treated as a gap rather than the end,
     * so switching which microphone you're looking at doesn't interrupt
     * playback.
     */
    this.continuousPlayback = options.continuousPlayback === true;
    this.followPlayhead =
      options.followPlayhead !== undefined ? options.followPlayhead : true;

    // Logging configuration
    this.enableLogging =
      options.enableLogging !== undefined ? options.enableLogging : false;
    this.logFunction =
      options.logFunction ||
      ((message) => console.log(`[AudioDAW] ${message}`));

    // DAW-wide today; copied onto every track, which is what drawing reads.
    this.frequencyHighlights = [];
    this.frequencyHighlightsOnLane = true;

    // Timeline and renderer
    this.timeline = new Timeline();
    this.timeline.pixelsPerSecond = options.pixelsPerSecond || 50;
    this.renderer = new ViewportRenderer(
      this.canvas,
      this.timeline,
      this,
      options.theme
    );

    if (this.minimapCanvas) {
      this.renderer.setMinimapCanvas(this.minimapCanvas);
    }

    if (options.interactionMode) {
      this.setInteractionMode(options.interactionMode);
    }

    if (options.regionDisplayMode) {
      this.setRegionDisplayMode(options.regionDisplayMode);
    }

    if (options.frequencyHighlights) {
      this.setFrequencyHighlights(options.frequencyHighlights);
    }

    if (options.frequencyHighlightsOnLane !== undefined) {
      this.setFrequencyHighlightsOnLane(options.frequencyHighlightsOnLane);
    }

    if (options.regionGroupNameDisplay) {
      this.setRegionGroupNameDisplay(options.regionGroupNameDisplay);
    }

    if (options.visibleTrackLimit !== undefined) {
      this.setVisibleTrackLimit(options.visibleTrackLimit);
    }

    // Event callbacks
    this.onLoadingStateChange = options.onLoadingStateChange || null;
    this.onPlayStateChange = options.onPlayStateChange || null;
    this.onTimeUpdate = options.onTimeUpdate || null;
    this.onSelectionChange = options.onSelectionChange || null;
    this.onRegionsChange = options.onRegionsChange || null;
    this.onRegionsDrag = options.onRegionsDrag || null;
    this.onTrackAdded = options.onTrackAdded || null;
    this.onTrackRemoved = options.onTrackRemoved || null;
    this.onTrackOffsetChange = options.onTrackOffsetChange || null;
    this.onTrackVolumeChange = options.onTrackVolumeChange || null;
    this.onTrackPanChange = options.onTrackPanChange || null;
    this.onTrackGainChange = options.onTrackGainChange || null;
    this.onTrackCompressorChange = options.onTrackCompressorChange || null;
    this.onTrackMuteChange = options.onTrackMuteChange || null;
    this.onTrackSoloChange = options.onTrackSoloChange || null;
    this.onTrackLayoutChange = options.onTrackLayoutChange || null;
    this.onTrackFrequencyRangeChange = options.onTrackFrequencyRangeChange || null;
    this.onFrequencyHighlightsChange = options.onFrequencyHighlightsChange || null;
    this.onTrackColorChange = options.onTrackColorChange || null;
    this.onTrackSpectrogramOpacityChange =
      options.onTrackSpectrogramOpacityChange || null;
    this.onTrackSpectrogramVariantChange =
      options.onTrackSpectrogramVariantChange || null;
    this.onPlaybackSpeedChange = options.onPlaybackSpeedChange || null;
    this.onInterpolatorChange = options.onInterpolatorChange || null;
    this.onPitchPreservationChange = options.onPitchPreservationChange || null;
    this.onInteractionModeChange = options.onInteractionModeChange || null;
    this.onTrackReplaced = options.onTrackReplaced || null;
    this.onZoomChange = options.onZoomChange || null;
    this.onDurationChange = options.onDurationChange || null;
    this.onScrollChange = options.onScrollChange || null;
    this.onActiveRegionGroupChange = options.onActiveRegionGroupChange || null;
    this.onRegionGroupsChange = options.onRegionGroupsChange || null;
    this.onRegionDisplayModeChange = options.onRegionDisplayModeChange || null;
  }

  // ============ LOGGING ============

  /**
   * Internal logging function
   * @private
   */
  log(message) {
    if (this.enableLogging && this.logFunction) {
      this.logFunction(message);
    }
  }

  /**
   * Set logging enabled state
   * @param {boolean} enabled - Enable or disable logging
   */
  setLoggingEnabled(enabled) {
    this.enableLogging = enabled;
  }

  /**
   * Get logging enabled state
   * @returns {boolean} Whether logging is enabled
   */
  isLoggingEnabled() {
    return this.enableLogging;
  }

  /**
   * Set custom log function
   * @param {Function} logFunction - Custom logging function that receives a message string
   */
  setLogFunction(logFunction) {
    this.logFunction = logFunction;
  }

  // ============ PUBLIC API METHODS ============

  /**
   * Get all tracks with their properties
   * @returns {Array} Array of track objects with id, fileName, duration, timeOffset, volume, isLoading, loadingPhase
   */
  getTracks() {
    return this.timeline.tracks.map((track) => ({
      id: track.id,
      fileName: track.fileName,
      duration: track.duration,
      timeOffset: track.timeOffset,
      sampleOffset: track.sampleOffset,
      sampleRate: track.sampleRate,
      originalSampleRate: track.originalSampleRate,
      volume: track.volume,
      pan: track.pan,
      gain: track.gain,
      useCompressor: track.useCompressor,
      muted: track.muted,
      soloed: track.soloed,
      spectrogramOpacity: track.spectrogramOpacity,
      hasSpectrogram: track.spectrogram !== null,
      isStreaming: track.audioBuffer === null && track.source !== null,
      canPlay: track.canPlay(),
      isLoading: track.isLoading,
      loadingPhase: track.loadingPhase,
      color: track.color,
    }));
  }

  /**
   * Add a new track with audio data
   * @param {File|ArrayBuffer} audioData - Audio file or buffer
   * @param {Object} options - Track options (fileName, timeOffset, volume, color)
   * @returns {Promise<string>} Track ID
   */
  async addTrack(audioData, options = {}) {
    await this.initAudioContext();

    const trackId = options.id || `track-${Date.now()}`;

    // Create track with loading state
    const newTrack = this.#createTrack(trackId);
    newTrack.fileName = options.fileName || "Untitled";
    newTrack.timeOffset = options.timeOffset || 0;
    newTrack.volume = options.volume !== undefined ? options.volume : 1.0;
    newTrack.pan = options.pan !== undefined ? options.pan : 0;
    newTrack.color = options.color || "#2196F3";
    newTrack.isLoading = true;
    newTrack.loadingPhase = "audio";

    // Add track to timeline immediately to show loading state
    this.timeline.tracks.push(newTrack);
    this.renderer.scheduleRender();

    if (this.onLoadingStateChange) {
      this.onLoadingStateChange(trackId, true, "audio");
    }

    try {
      // Load audio data
      let arrayBuffer;
      if (audioData instanceof File) {
        arrayBuffer = await audioData.arrayBuffer();
      } else {
        arrayBuffer = audioData;
      }

      // Extract original sample rate before browser resampling
      const originalSampleRate = extractOriginalSampleRate(arrayBuffer);

      const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);

      // Store original sample rate in track
      if (originalSampleRate) {
        newTrack.originalSampleRate = originalSampleRate;
        this.log(
          `Track "${newTrack.fileName}" original sample rate: ${originalSampleRate} Hz (browser resampled to: ${audioBuffer.sampleRate} Hz)`
        );
      } else {
        // Fall back to audioBuffer sample rate if we couldn't extract original
        newTrack.originalSampleRate = audioBuffer.sampleRate;
        this.log(
          `Track "${newTrack.fileName}" sample rate: ${audioBuffer.sampleRate} Hz (original rate could not be determined)`
        );
      }

      // Set project sample rate from first track
      this.timeline.setProjectSampleRate(audioBuffer.sampleRate);

      this.#buildTrackChain(newTrack);
      const preGainNode = newTrack.preGainNode;

      // Switch to waveform generation phase
      newTrack.loadingPhase = "waveform";
      this.renderer.scheduleRender();

      if (this.onLoadingStateChange) {
        this.onLoadingStateChange(trackId, true, "waveform");
      }

      // Generate waveform peaks
      const waveform = new LocalWaveform(audioBuffer);

      // Update track
      newTrack.setAudioBuffer(audioBuffer);
      newTrack.waveform = waveform;
      newTrack.source = new BufferTrackSource(newTrack, this.audioContext, preGainNode);
      newTrack.isLoading = false;
      newTrack.loadingPhase = null;

      // Joining a transport that's already running.
      if (this.isPlaying) {
        newTrack.source.start(this.currentTime, this.playbackSpeed);
      }

      // Derive the sample offset now that the source rate is known. This has to
      // use the file's own rate, not the decoded buffer's — they differ whenever
      // the browser resampled on decode.
      newTrack.setTimeOffset(newTrack.timeOffset);

      // Recalculate timeline duration
      this.recalculateDuration();

      this.renderer.scheduleRender();

      if (this.onLoadingStateChange) {
        this.onLoadingStateChange(trackId, false, null);
      }

      if (this.onTrackAdded) {
        this.onTrackAdded(trackId, {
          id: newTrack.id,
          fileName: newTrack.fileName,
          duration: newTrack.duration,
          timeOffset: newTrack.timeOffset,
          volume: newTrack.volume,
          color: newTrack.color,
        });
      }

      return trackId;
    } catch (error) {
      // Remove track on error
      const index = this.timeline.tracks.findIndex((t) => t.id === trackId);
      if (index !== -1) {
        this.timeline.tracks.splice(index, 1);
      }

      if (this.onLoadingStateChange) {
        this.onLoadingStateChange(trackId, false, null);
      }

      this.renderer.scheduleRender();
      throw error;
    }
  }

  /**
   * Add a track backed by precomputed analysis data on the server.
   *
   * Resolves as soon as the waveform can be drawn — typically one manifest
   * fetch plus one overview-peaks fetch, a few KB regardless of how long the
   * asset is. Finer peak detail streams in as the viewport needs it.
   *
   * The track is display-only until streamed playback lands: `canPlay()` is
   * false and `play()` skips it.
   *
   * @param {string} manifestUrl - URL of the asset's manifest.json
   * @param {Object} options - Track options (id, fileName, timeOffset, color)
   * @returns {Promise<string>} Track ID
   */
  async addRemoteTrack(manifestUrl, options = {}) {
    const fetchOptions = options.fetchOptions ?? this.fetchOptions;
    const response = await remoteFetch(manifestUrl, fetchOptions);
    if (!response.ok) {
      throw new Error(`Manifest fetch failed: ${response.status} ${response.statusText}`);
    }

    const manifest = await response.json();
    if (manifest.status === "processing") {
      const percent = Math.round((manifest.progress || 0) * 100);
      throw new Error(`Analysis is still being generated (${percent}%)`);
    }
    if (!manifest.peaks) {
      throw new Error(`Manifest for "${manifest.assetId}" has no peak data`);
    }

    // One sample rate per project: a mismatch would put every sample offset in
    // this track on a different timeline from the others.
    const projectSampleRate = this.timeline.projectSampleRate;
    if (projectSampleRate !== null && projectSampleRate !== manifest.sampleRate) {
      throw new Error(
        `Track sample rate ${manifest.sampleRate} Hz does not match the project rate ` +
          `${projectSampleRate} Hz. A project cannot mix sample rates.`
      );
    }

    const trackId = options.id || `track-${Date.now()}`;
    const track = this.#createTrack(trackId);

    track.fileName = options.fileName || manifest.assetId;
    track.color = options.color || "#2196F3";
    track.volume = options.volume !== undefined ? options.volume : 1.0;

    // Nothing resamples a remote track, so the decoded rate and the file rate
    // are the same — which is what makes sample offsets unambiguous.
    track.sampleRate = manifest.sampleRate;
    track.originalSampleRate = manifest.sampleRate;
    track.sampleCount = manifest.sampleCount;
    track.duration = manifest.duration;
    track.maxAmplitude = manifest.maxAmplitude ?? null;
    track.setTimeOffset(options.timeOffset || 0);

    track.isLoading = true;
    track.loadingPhase = "waveform";

    this.timeline.setProjectSampleRate(manifest.sampleRate);
    this.timeline.tracks.push(track);
    this.recalculateDuration();
    this.renderer.scheduleRender();

    if (this.onLoadingStateChange) {
      this.onLoadingStateChange(trackId, true, "waveform");
    }

    try {
      track.waveform = new RemoteWaveform(manifest, manifestUrl, {
        fetchOptions,
        onChange: () => this.renderer?.scheduleRender(),
      });

      if (manifest.spectrogram) {
        track.spectrogram = new RemoteSpectrogram(manifest, manifestUrl, {
          fetchOptions,
          variantId: options.spectrogramVariant,
          colormap: this.renderer.theme.spectrogramColormap,
          displayFloor: this.renderer.theme.spectrogramDisplayFloor,
          displayCeiling: this.renderer.theme.spectrogramDisplayCeiling,
          onChange: () => this.renderer?.scheduleRender(),
        });
        track.spectrogramOpacity = options.spectrogramOpacity ?? 0;
      }

      await track.waveform.loadOverview();

      // Streamed audio is optional: a manifest may carry analysis long before
      // the PCM endpoint is available, and the track is still fully usable.
      if (manifest.pcm) {
        await this.initAudioContext();
        this.#buildTrackChain(track);
        track.source = new StreamingTrackSource(
          track,
          manifest,
          manifestUrl,
          this.audioContext,
          track.preGainNode,
          {
            interpolator: this.interpolatorId,
            preservePitch: this.preservePitch,
            pitchRange: this.pitchRange,
            fetchOptions,
            onPosition: () => this.renderer?.scheduleRender(),
          }
        );
        await track.source.prepare();
      }

      track.isLoading = false;
      track.loadingPhase = null;

      // Joining a transport that's already running: start at the current
      // position rather than waiting for the next seek.
      if (this.isPlaying && track.source) {
        track.source.start(this.currentTime, this.playbackSpeed);
      }

      this.log(
        `Remote track "${track.fileName}": ${manifest.sampleRate} Hz, ` +
          `${manifest.duration.toFixed(1)}s, levels 1/${manifest.peaks.levels.join(", 1/")}`
      );
      if (track.maxAmplitude > 1) {
        this.log(
          `Track "${track.fileName}" peaks at ${track.maxAmplitude.toFixed(3)} — above full scale, ` +
            `waveform display is clamped`
        );
      }

      this.renderer.scheduleRender();

      if (this.onLoadingStateChange) {
        this.onLoadingStateChange(trackId, false, null);
      }
      // A replacement can be shorter than what it replaced — a channel that ran
      // a second or two short in the same multitrack recording. If the playhead
      // is already past the end of everything loaded, there's nothing to play.
      if (this.isPlaying && this.currentTime >= this.timeline.duration) {
        this.log(
          `Playhead at ${this.currentTime.toFixed(2)}s is past the end of ` +
            `"${track.fileName}" (${track.duration.toFixed(2)}s) — stopping`
        );
        this.stop();
      }

      if (this.onTrackAdded) {
        this.onTrackAdded(trackId, {
          id: track.id,
          fileName: track.fileName,
          duration: track.duration,
          timeOffset: track.timeOffset,
          volume: track.volume,
          color: track.color,
        });
      }

      return trackId;
    } catch (error) {
      const index = this.timeline.tracks.findIndex((t) => t.id === trackId);
      if (index !== -1) this.timeline.tracks.splice(index, 1);

      this.recalculateDuration();
      if (this.onLoadingStateChange) {
        this.onLoadingStateChange(trackId, false, null);
      }
      this.renderer.scheduleRender();
      throw error;
    }
  }

  /**
   * Swap one track for another without interrupting playback.
   *
   * The incoming track is added silent and hidden, given a moment to buffer the
   * position the playhead is actually at, then crossfaded in while the outgoing
   * one fades out — so the old microphone keeps sounding out of its own ring
   * buffer right up until the new one can take over. Only then is it removed.
   *
   * Set `visibleTrackLimit` to 1 to keep the overlap off screen as well;
   * otherwise the lane splits in two for the duration of the handover.
   *
   * A project can't mix sample rates, so if the replacement runs at a different
   * rate there is nothing to overlap with: the outgoing track is removed first
   * and the swap is a hard cut. Playback still rides through the gap when
   * `continuousPlayback` is on.
   *
   * @param {string} trackId - Track to replace
   * @param {string} manifestUrl - Manifest for the replacement
   * @param {Object} options - As `addRemoteTrack`, plus `crossfade` in seconds
   * @returns {Promise<string>} The new track's ID
   */
  async replaceTrack(trackId, manifestUrl, options = {}) {
    const outgoing = this.timeline.tracks.find((t) => t.id === trackId);
    if (!outgoing) {
      throw new Error(`Track ${trackId} not found`);
    }

    const crossfade = options.crossfade ?? 0.08;
    const targetVolume = options.volume !== undefined ? options.volume : 1;
    const wasPlaying = this.isPlaying;

    let incomingId;
    try {
      // Silent to begin with, so the overlap is never two microphones at once.
      incomingId = await this.addRemoteTrack(manifestUrl, { ...options, volume: 0 });
    } catch (error) {
      if (!/sample rate/i.test(error.message)) throw error;

      // Different rate: no overlap is possible, so give up continuity rather
      // than the swap.
      this.log(`Replacement runs at a different sample rate — swapping without a crossfade`);
      this.removeTrack(trackId);
      const hardSwapId = await this.addRemoteTrack(manifestUrl, options);
      this.onTrackReplaced?.(trackId, hardSwapId, { crossfaded: false });
      return hardSwapId;
    }

    const incoming = this.timeline.tracks.find((t) => t.id === incomingId);
    let crossfaded = false;

    if (wasPlaying && this.isPlaying && crossfade > 0) {
      // Wait for the replacement to actually have the audio under the playhead.
      // Bounded: a slow server should cost a rougher handover, not a hang.
      await this.#waitForAudioAt(incoming, this.currentTime, 1000);

      this.#rampTrackGain(outgoing, 0, crossfade);
      this.#rampTrackGain(incoming, targetVolume, crossfade);
      incoming.volume = targetVolume;

      await new Promise((resolve) => setTimeout(resolve, crossfade * 1000));
      crossfaded = true;
    } else {
      this.setTrackVolume(incomingId, targetVolume);
    }

    this.removeTrack(trackId);
    this.onTrackReplaced?.(trackId, incomingId, { crossfaded });
    return incomingId;
  }

  /**
   * Ramp a track's output gain linearly.
   *
   * Linear rather than equal-power on purpose: two microphones on the same
   * source are correlated, and an equal-power curve would bump the level
   * through the middle of the fade.
   * @private
   */
  #rampTrackGain(track, to, seconds) {
    if (!track?.gainNode || !this.audioContext) return;

    const now = this.audioContext.currentTime;
    const gain = track.gainNode.gain;

    gain.cancelScheduledValues(now);
    gain.setValueAtTime(gain.value, now);
    // Through the audibility filter, so a crossfade into a muted or un-soloed
    // track fades it up to silence rather than quietly overriding the mute.
    gain.linearRampToValueAtTime(this.#audibleVolume(track, to), now + seconds);
  }

  /** Resolve once a track can play `time`, or after `timeout` ms. @private */
  async #waitForAudioAt(track, time, timeout) {
    const deadline = Date.now() + timeout;
    const local = time - track.timeOffset;

    while (Date.now() < deadline) {
      const ranges = track.source?.getBufferedRanges?.() ?? [];
      if (ranges.some((r) => r.start <= local && r.end >= local)) return true;
      await new Promise((resolve) => setTimeout(resolve, 20));
    }
    return false;
  }

  /**
   * How many tracks to draw, or null for all of them.
   *
   * Display only — hidden tracks still play, which is what makes an overlapping
   * swap invisible rather than a lane that splits in two and snaps back.
   *
   * @param {number|null} limit
   */
  setVisibleTrackLimit(limit) {
    this.renderer.visibleTrackLimit = limit === null ? null : Math.max(0, Math.floor(limit));
    this.renderer.scheduleRender();
  }

  /**
   * @returns {number|null}
   */
  getVisibleTrackLimit() {
    return this.renderer.visibleTrackLimit;
  }

  /**
   * Replace audio data on an existing track (keeps offset and settings)
   * @param {string} trackId - Track ID
   * @param {File|ArrayBuffer} audioData - New audio data
   * @returns {Promise<void>}
   */
  async setTrackAudio(trackId, audioData) {
    const track = this.timeline.tracks.find((t) => t.id === trackId);
    if (!track) {
      throw new Error(`Track ${trackId} not found`);
    }

    track.isLoading = true;
    track.loadingPhase = "audio";
    this.renderer.scheduleRender();

    if (this.onLoadingStateChange) {
      this.onLoadingStateChange(trackId, true, "audio");
    }

    try {
      // Load audio data
      let arrayBuffer;
      if (audioData instanceof File) {
        arrayBuffer = await audioData.arrayBuffer();
      } else {
        arrayBuffer = audioData;
      }

      // Extract original sample rate before browser resampling
      const originalSampleRate = extractOriginalSampleRate(arrayBuffer);

      const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);

      // Store original sample rate in track
      if (originalSampleRate) {
        track.originalSampleRate = originalSampleRate;
        this.log(
          `Track "${track.fileName}" original sample rate: ${originalSampleRate} Hz (browser resampled to: ${audioBuffer.sampleRate} Hz)`
        );
      } else {
        // Fall back to audioBuffer sample rate if we couldn't extract original
        track.originalSampleRate = audioBuffer.sampleRate;
        this.log(
          `Track "${track.fileName}" sample rate: ${audioBuffer.sampleRate} Hz (original rate could not be determined)`
        );
      }

      // Switch to waveform generation phase
      track.loadingPhase = "waveform";
      this.renderer.scheduleRender();

      if (this.onLoadingStateChange) {
        this.onLoadingStateChange(trackId, true, "waveform");
      }

      // Generate new waveform peaks
      const waveform = new LocalWaveform(audioBuffer);

      // Swap the audio out from under the source
      track.source?.stop();

      // Update track (keeps timeOffset, volume, color, gainNode)
      track.setAudioBuffer(audioBuffer);
      track.waveform = waveform;
      track.isLoading = false;
      track.loadingPhase = null;

      // Recalculate timeline duration (audio length may have changed)
      this.recalculateDuration();

      // Resume this track if the transport is running
      if (this.isPlaying) {
        track.source?.start(this.currentTime, this.playbackSpeed);
      }

      this.renderer.scheduleRender();

      if (this.onLoadingStateChange) {
        this.onLoadingStateChange(trackId, false, null);
      }
    } catch (error) {
      track.isLoading = false;
      track.loadingPhase = null;

      if (this.onLoadingStateChange) {
        this.onLoadingStateChange(trackId, false, null);
      }

      this.renderer.scheduleRender();
      throw error;
    }
  }

  /**
   * Set volume for a specific track
   * @param {string} trackId - Track ID
   * @param {number} volume - Volume level (0.0 to 1.0)
   */
  setTrackVolume(trackId, volume) {
    const track = this.timeline.tracks.find((t) => t.id === trackId);
    if (!track) {
      throw new Error(`Track ${trackId} not found`);
    }

    volume = Math.max(0, Math.min(1, volume));
    track.volume = volume;
    this.#applyAudibility(track);

    if (this.onTrackVolumeChange) {
      this.onTrackVolumeChange(trackId, volume);
    }
  }

  /**
   * Get volume for a specific track
   * @param {string} trackId - Track ID
   * @returns {number} Volume level (0.0 to 1.0)
   */
  getTrackVolume(trackId) {
    const track = this.timeline.tracks.find((t) => t.id === trackId);
    if (!track) {
      throw new Error(`Track ${trackId} not found`);
    }
    return track.volume;
  }

  /**
   * Set pan for a specific track
   * @param {string} trackId - Track ID
   * @param {number} pan - Pan value (-1.0 = left, 0.0 = center, 1.0 = right)
   */
  setTrackPan(trackId, pan) {
    const track = this.timeline.tracks.find((t) => t.id === trackId);
    if (!track) {
      throw new Error(`Track ${trackId} not found`);
    }

    pan = Math.max(-1, Math.min(1, pan));
    track.pan = pan;

    if (track.panNode) {
      track.panNode.pan.setTargetAtTime(
        pan,
        this.audioContext.currentTime,
        0.01
      );
    }

    if (this.onTrackPanChange) {
      this.onTrackPanChange(trackId, pan);
    }
  }

  /**
   * Get pan for a specific track
   * @param {string} trackId - Track ID
   * @returns {number} Pan value (-1.0 to 1.0)
   */
  getTrackPan(trackId) {
    const track = this.timeline.tracks.find((t) => t.id === trackId);
    if (!track) {
      throw new Error(`Track ${trackId} not found`);
    }
    return track.pan;
  }

  /**
   * Set gain for a specific track (in dB)
   * @param {string} trackId - Track ID
   * @param {number} gainDb - Gain in decibels (-40 to +40, 0 = unity gain)
   */
  setTrackGain(trackId, gainDb) {
    const track = this.timeline.tracks.find((t) => t.id === trackId);
    if (!track) {
      throw new Error(`Track ${trackId} not found`);
    }

    gainDb = Math.max(-40, Math.min(40, gainDb));
    track.gain = gainDb;

    if (track.preGainNode) {
      const gainLinear = Math.pow(10, gainDb / 20); // Convert dB to linear
      track.preGainNode.gain.setTargetAtTime(
        gainLinear,
        this.audioContext.currentTime,
        0.01
      );
    }

    // Schedule a render since gain affects waveform display when waveformGainScaling is enabled
    this.renderer.scheduleRender();

    if (this.onTrackGainChange) {
      this.onTrackGainChange(trackId, gainDb);
    }
  }

  /**
   * Get gain for a specific track (in dB)
   * @param {string} trackId - Track ID
   * @returns {number} Gain in decibels
   */
  getTrackGain(trackId) {
    const track = this.timeline.tracks.find((t) => t.id === trackId);
    if (!track) {
      throw new Error(`Track ${trackId} not found`);
    }
    return track.gain;
  }

  /**
   * Enable or disable compressor for a specific track
   * @param {string} trackId - Track ID
   * @param {boolean} enabled - Enable compressor
   * @param {Object} settings - Optional compressor settings (threshold, knee, ratio, attack, release)
   */
  setTrackCompressor(trackId, enabled, settings = {}) {
    const track = this.timeline.tracks.find((t) => t.id === trackId);
    if (!track) {
      throw new Error(`Track ${trackId} not found`);
    }

    // If switching compressor state, need to rebuild audio chain
    if (track.useCompressor !== enabled) {
      track.useCompressor = enabled;

      // Disconnect existing chain
      if (track.panNode) track.panNode.disconnect();
      if (track.gainNode) track.gainNode.disconnect();
      if (track.limiterNode) track.limiterNode.disconnect();
      if (track.compressorNode) track.compressorNode.disconnect();
      if (track.preGainNode) track.preGainNode.disconnect();

      // Recreate compressor if enabling
      if (enabled) {
        track.compressorNode = this.audioContext.createDynamicsCompressor();
        track.compressorNode.threshold.value = settings.threshold || -24;
        track.compressorNode.knee.value = settings.knee || 30;
        track.compressorNode.ratio.value = settings.ratio || 12;
        track.compressorNode.attack.value = settings.attack || 0.003;
        track.compressorNode.release.value = settings.release || 0.25;
      } else {
        track.compressorNode = null;
      }

      // Reconnect chain. The limiter is always in the path, after whatever
      // compressor state we just landed on and before volume — cranking gain
      // or the compressor's makeup gain still can't blow out the output.
      let currentNode = track.preGainNode;
      if (track.compressorNode) {
        track.preGainNode.connect(track.compressorNode);
        currentNode = track.compressorNode;
      }
      currentNode.connect(track.limiterNode);
      track.limiterNode.connect(track.gainNode);
      track.gainNode.connect(track.panNode);
      track.panNode.connect(this.audioContext.destination);

      // Restart playback if playing
      if (this.isPlaying) {
        const wasPlaying = true;
        this.stop();
        if (wasPlaying) {
          this.play();
        }
      }
    } else if (enabled && track.compressorNode && settings) {
      // Just update settings
      if (settings.threshold !== undefined)
        track.compressorNode.threshold.value = settings.threshold;
      if (settings.knee !== undefined)
        track.compressorNode.knee.value = settings.knee;
      if (settings.ratio !== undefined)
        track.compressorNode.ratio.value = settings.ratio;
      if (settings.attack !== undefined)
        track.compressorNode.attack.value = settings.attack;
      if (settings.release !== undefined)
        track.compressorNode.release.value = settings.release;
    }

    if (this.onTrackCompressorChange) {
      this.onTrackCompressorChange(trackId, enabled, settings);
    }
  }

  /**
   * Check if compressor is enabled for a track
   * @param {string} trackId - Track ID
   * @returns {boolean} Whether compressor is enabled
   */
  getTrackCompressor(trackId) {
    const track = this.timeline.tracks.find((t) => t.id === trackId);
    if (!track) {
      throw new Error(`Track ${trackId} not found`);
    }
    return track.useCompressor;
  }

  /**
   * Set color for a specific track
   * @param {string} trackId - Track ID
   * @param {string} color - CSS color string
   */
  setTrackColor(trackId, color) {
    const track = this.timeline.tracks.find((t) => t.id === trackId);
    if (!track) {
      throw new Error(`Track ${trackId} not found`);
    }

    track.color = color;
    this.renderer.scheduleRender();

    if (this.onTrackColorChange) {
      this.onTrackColorChange(trackId, color);
    }
  }


  // ============ MUTE / SOLO ============

  /**
   * Whether a track should be making sound right now.
   *
   * Solo is a property of the *timeline*, not of a track: as soon as anything is
   * soloed, everything that isn't goes quiet. So this can't be answered from one
   * track alone, and every mute/solo change has to re-apply to all of them.
   *
   * Mute wins over solo — a track that is both is silent, which is what lets a
   * host leave mutes in place while auditioning a solo.
   * @private
   */
  #isAudible(track) {
    return this.timeline.isTrackAudible(track);
  }

  /**
   * The gain value a track's output node should hold.
   * @private
   */
  #audibleVolume(track, volume = track.volume) {
    return this.#isAudible(track) ? volume : 0;
  }

  /**
   * Push a track's audibility to its gain node.
   *
   * Ramped rather than stepped: a gain node jumped to zero on a non-zero sample
   * clicks, and mute is exactly the control someone hits repeatedly.
   * @private
   */
  #applyAudibility(track) {
    if (!track.gainNode || !this.audioContext) return;
    track.gainNode.gain.setTargetAtTime(
      this.#audibleVolume(track),
      this.audioContext.currentTime,
      0.01
    );
  }

  /** Re-apply audibility everywhere. @private */
  #applyAudibilityToAll() {
    for (const track of this.timeline.tracks) this.#applyAudibility(track);
  }

  /**
   * Silence a track without touching its volume.
   *
   * Independent of volume so a host can mute and unmute without having to
   * remember what the fader was at.
   *
   * @param {string} trackId - Track ID
   * @param {boolean} muted
   */
  setTrackMuted(trackId, muted) {
    const track = this.timeline.tracks.find((t) => t.id === trackId);
    if (!track) {
      throw new Error(`Track ${trackId} not found`);
    }

    track.muted = !!muted;
    this.#applyAudibility(track);
    this.renderer.scheduleRender();

    this.onTrackMuteChange?.(trackId, track.muted);
  }

  /**
   * @param {string} trackId - Track ID
   * @returns {boolean} Whether the track is muted
   */
  getTrackMuted(trackId) {
    const track = this.timeline.tracks.find((t) => t.id === trackId);
    if (!track) {
      throw new Error(`Track ${trackId} not found`);
    }
    return track.muted;
  }

  /**
   * Solo a track: while anything is soloed, everything that isn't goes quiet.
   *
   * Several tracks can be soloed at once — soloing a second one adds it rather
   * than replacing the first. `clearSolo()` is how you get back to everything
   * audible.
   *
   * @param {string} trackId - Track ID
   * @param {boolean} soloed
   */
  setTrackSolo(trackId, soloed) {
    const track = this.timeline.tracks.find((t) => t.id === trackId);
    if (!track) {
      throw new Error(`Track ${trackId} not found`);
    }

    track.soloed = !!soloed;
    // Every track, not just this one: the last solo being switched off is what
    // makes all the others audible again.
    this.#applyAudibilityToAll();
    this.renderer.scheduleRender();

    this.onTrackSoloChange?.(trackId, track.soloed);
  }

  /**
   * @param {string} trackId - Track ID
   * @returns {boolean} Whether the track is soloed
   */
  getTrackSolo(trackId) {
    const track = this.timeline.tracks.find((t) => t.id === trackId);
    if (!track) {
      throw new Error(`Track ${trackId} not found`);
    }
    return track.soloed;
  }

  /**
   * Un-solo everything, in one call and one notification per track.
   */
  clearSolo() {
    const cleared = this.timeline.tracks.filter((t) => t.soloed);
    for (const track of cleared) track.soloed = false;

    this.#applyAudibilityToAll();
    this.renderer.scheduleRender();

    for (const track of cleared) this.onTrackSoloChange?.(track.id, false);
  }

  /**
   * Whether a track is actually making sound, accounting for both its own mute
   * and any solo elsewhere on the timeline.
   *
   * This is what a mixer strip should dim, rather than `getTrackMuted` — a track
   * silenced because something else is soloed looks no different to the ear.
   *
   * @param {string} trackId - Track ID
   * @returns {boolean}
   */
  isTrackAudible(trackId) {
    const track = this.timeline.tracks.find((t) => t.id === trackId);
    if (!track) {
      throw new Error(`Track ${trackId} not found`);
    }
    return this.#isAudible(track);
  }

  // ============ SPECTROGRAM ============

  /**
   * Blend a spectrogram over the track's waveform.
   *
   * The spectrogram occupies the same lane as the waveform: 0 shows the
   * waveform alone, 1 covers it completely, and anything between blends. Only
   * remote tracks whose manifest includes spectrogram analysis have one —
   * `hasSpectrogram()` says which.
   *
   * @param {string} trackId - Track ID
   * @param {number} opacity - 0.0 to 1.0
   */
  setTrackSpectrogramOpacity(trackId, opacity) {
    const track = this.timeline.tracks.find((t) => t.id === trackId);
    if (!track) {
      throw new Error(`Track ${trackId} not found`);
    }

    track.spectrogramOpacity = Math.max(0, Math.min(1, opacity));
    this.renderer.scheduleRender();

    if (this.onTrackSpectrogramOpacityChange) {
      this.onTrackSpectrogramOpacityChange(trackId, track.spectrogramOpacity);
    }
  }

  /**
   * Get the spectrogram blend for a track
   * @param {string} trackId - Track ID
   * @returns {number} Opacity from 0.0 to 1.0
   */
  getTrackSpectrogramOpacity(trackId) {
    const track = this.timeline.tracks.find((t) => t.id === trackId);
    if (!track) {
      throw new Error(`Track ${trackId} not found`);
    }
    return track.spectrogramOpacity;
  }

  /**
   * Whether a track has spectrogram data available
   * @param {string} trackId - Track ID
   * @returns {boolean}
   */
  hasSpectrogram(trackId) {
    const track = this.timeline.tracks.find((t) => t.id === trackId);
    if (!track) {
      throw new Error(`Track ${trackId} not found`);
    }
    return track.spectrogram !== null;
  }

  /**
   * Describe a track's spectrogram analysis — frequency range, bin count, and
   * the dB window currently displayed. The frequency ceiling is per-asset
   * (Nyquist-limited), so anything labelling an axis must read it rather than
   * assume 20 kHz.
   *
   * @param {string} trackId - Track ID
   * @returns {Object|null} {minFrequency, maxFrequency, bins, colormap, displayFloor, displayCeiling}
   */
  getSpectrogramInfo(trackId) {
    const track = this.timeline.tracks.find((t) => t.id === trackId);
    if (!track) {
      throw new Error(`Track ${trackId} not found`);
    }
    if (!track.spectrogram) return null;

    const { spectrogram } = track;
    const variant = spectrogram.variant;

    return {
      minFrequency: spectrogram.minFrequency,
      maxFrequency: spectrogram.maxFrequency,
      binScale: spectrogram.binScale,
      bins: spectrogram.bins,
      colormap: spectrogram.colormap,
      displayFloor: spectrogram.displayFloor,
      displayCeiling: spectrogram.displayCeiling,
      variantId: spectrogram.variantId,
      fftSize: variant.fftSize,
      windowMs: variant.windowMs,
      overlap: variant.overlap,
    };
  }

  /**
   * Every analysis configuration the asset offers.
   *
   * The right analysis window depends on the language and speech pattern, so
   * this is a view-time choice. Each entry reports both `windowMs` and
   * `fftSize` — window duration is what the analysis depends on and what
   * transfers across sample rates, FFT size is what a DSP-minded user reaches
   * for — so a menu entry can read "21 ms → 512 fft".
   *
   * @param {string} trackId - Track ID
   * @returns {Array} [{id, fftSize, windowMs, window, overlap, columnsPerSecond, isDefault}]
   */
  getSpectrogramVariants(trackId) {
    const track = this.timeline.tracks.find((t) => t.id === trackId);
    if (!track) {
      throw new Error(`Track ${trackId} not found`);
    }
    return track.spectrogram ? track.spectrogram.variants : [];
  }

  /**
   * Switch a track's spectrogram to a different analysis variant.
   *
   * Tiles for the previous variant stay cached, so flipping between two to
   * compare them is instant after the first look at each.
   *
   * @param {string} trackId - Track ID
   * @param {string} variantId - id from getSpectrogramVariants()
   */
  setTrackSpectrogramVariant(trackId, variantId) {
    const track = this.timeline.tracks.find((t) => t.id === trackId);
    if (!track) {
      throw new Error(`Track ${trackId} not found`);
    }
    if (!track.spectrogram) {
      throw new Error(`Track ${trackId} has no spectrogram data`);
    }

    track.spectrogram.setVariant(variantId);
    this.renderer.scheduleRender();

    const variant = track.spectrogram.variant;
    this.log(
      `Spectrogram variant "${variantId}": ${variant.windowMs} ms window, ` +
        `${variant.fftSize}-point FFT, ${variant.overlap}`
    );

    if (this.onTrackSpectrogramVariantChange) {
      this.onTrackSpectrogramVariantChange(trackId, variantId);
    }
  }

  /**
   * Change the spectrogram colour ramp and/or displayed dB window.
   *
   * Applies to every track that has a spectrogram. Cached tiles are recoloured
   * from their grayscale source, so nothing is refetched.
   *
   * @param {Object} appearance - {colormap, displayFloor, displayCeiling}
   */
  setSpectrogramAppearance(appearance = {}) {
    if (appearance.colormap !== undefined) {
      this.renderer.theme.spectrogramColormap = appearance.colormap;
    }
    if (appearance.displayFloor !== undefined) {
      this.renderer.theme.spectrogramDisplayFloor = appearance.displayFloor;
    }
    if (appearance.displayCeiling !== undefined) {
      this.renderer.theme.spectrogramDisplayCeiling = appearance.displayCeiling;
    }

    for (const track of this.timeline.tracks) {
      track.spectrogram?.setAppearance(appearance);
    }

    this.renderer.scheduleRender();
  }

  /**
   * Get color for a specific track
   * @param {string} trackId - Track ID
   * @returns {string} CSS color string
   */
  getTrackColor(trackId) {
    const track = this.timeline.tracks.find((t) => t.id === trackId);
    if (!track) {
      throw new Error(`Track ${trackId} not found`);
    }
    return track.color;
  }

  /**
   * Remove a track
   * @param {string} trackId - Track ID
   */
  removeTrack(trackId) {
    const index = this.timeline.tracks.findIndex((t) => t.id === trackId);
    if (index === -1) {
      throw new Error(`Track ${trackId} not found`);
    }

    const track = this.timeline.tracks[index];

    // Rulers outlive nothing: a scale still bound to a removed track would keep
    // its ResizeObserver alive and redraw against a lane that no longer exists.
    this.renderer.detachScales(trackId);

    // Stop and tear down its playback source
    if (track.source) {
      track.source.destroy();
      track.source = null;
    }

    // Disconnect audio nodes
    if (track.panNode) track.panNode.disconnect();
    if (track.gainNode) track.gainNode.disconnect();
    if (track.limiterNode) track.limiterNode.disconnect();
    if (track.compressorNode) track.compressorNode.disconnect();
    if (track.preGainNode) track.preGainNode.disconnect();

    // Stop any in-flight peak and tile fetches
    if (track.waveform) {
      track.waveform.destroy();
      track.waveform = null;
    }
    if (track.spectrogram) {
      track.spectrogram.destroy();
      track.spectrogram = null;
    }

    // Remove from timeline
    this.timeline.tracks.splice(index, 1);

    // An empty timeline has no project sample rate. Without this, removing every
    // track and loading an asset at a different rate would be rejected by the
    // one-rate-per-project guard.
    if (this.timeline.tracks.length === 0) {
      this.timeline.projectSampleRate = null;
    }

    // Recalculate duration
    this.recalculateDuration();

    this.renderer.scheduleRender();

    if (this.onTrackRemoved) {
      this.onTrackRemoved(trackId);
    }
  }

  /**
   * Set time offset for a track
   * @param {string} trackId - Track ID
   * @param {number} timeOffset - Time offset in seconds
   */
  setTrackOffset(trackId, timeOffset) {
    const track = this.timeline.tracks.find((t) => t.id === trackId);
    if (!track) {
      throw new Error(`Track ${trackId} not found`);
    }

    // Go through setTimeOffset so sampleOffset — the source of truth — follows.
    track.setTimeOffset(Math.max(0, timeOffset));

    // Recalculate duration
    this.recalculateDuration();

    // Re-align this track against the transport; the others are untouched.
    track.source?.seek(this.currentTime, this.playbackSpeed, this.isPlaying);

    this.renderer.scheduleRender();

    if (this.onTrackOffsetChange) {
      this.onTrackOffsetChange(trackId, track.timeOffset);
    }
  }

  /**
   * Set a track's offset in samples.
   *
   * Samples are the source of truth for alignment — `timeOffset` is derived
   * from this, not the other way round — so anything doing precise alignment
   * should drive this rather than the seconds form. The rate is the file's own,
   * which is not necessarily the rate the audio decoded to.
   *
   * @param {string} trackId - Track ID
   * @param {number} sampleOffset - Offset in the file's own sample rate
   */
  setTrackSampleOffset(trackId, sampleOffset) {
    const track = this.timeline.tracks.find((t) => t.id === trackId);
    if (!track) {
      throw new Error(`Track ${trackId} not found`);
    }

    track.setSampleOffset(Math.max(0, Math.round(sampleOffset)));
    this.recalculateDuration();
    track.source?.seek(this.currentTime, this.playbackSpeed, this.isPlaying);
    this.renderer.scheduleRender();

    if (this.onTrackOffsetChange) {
      this.onTrackOffsetChange(trackId, track.timeOffset);
    }
  }

  /**
   * @param {string} trackId - Track ID
   * @returns {number} Offset in the file's own sample rate
   */
  getTrackSampleOffset(trackId) {
    const track = this.timeline.tracks.find((t) => t.id === trackId);
    if (!track) {
      throw new Error(`Track ${trackId} not found`);
    }
    return track.sampleOffset;
  }

  /**
   * @param {string} trackId - Track ID
   * @returns {number} Offset in seconds
   */
  getTrackOffset(trackId) {
    const track = this.timeline.tracks.find((t) => t.id === trackId);
    if (!track) {
      throw new Error(`Track ${trackId} not found`);
    }
    return track.timeOffset;
  }

  /**
   * Rename a track (the label drawn on its lane)
   * @param {string} trackId - Track ID
   * @param {string} name
   */
  setTrackName(trackId, name) {
    const track = this.timeline.tracks.find((t) => t.id === trackId);
    if (!track) {
      throw new Error(`Track ${trackId} not found`);
    }
    track.fileName = name;
    this.renderer.scheduleRender();
  }

  /**
   * @param {string} trackId - Track ID
   * @returns {string} Track label
   */
  getTrackName(trackId) {
    const track = this.timeline.tracks.find((t) => t.id === trackId);
    if (!track) {
      throw new Error(`Track ${trackId} not found`);
    }
    return track.fileName;
  }

  /**
   * Start playback from the current position.
   */
  async play() {
    if (this.isPlaying) return;

    await this.initAudioContext();
    if (this.audioContext.state === "suspended") {
      await this.audioContext.resume();
    }

    // Get audio under the playhead before starting, so playback doesn't open
    // with a fraction of a second of silence and an underrun.
    await Promise.all(
      this.timeline.tracks
        .filter((track) => !track.isLoading && track.source?.prebuffer)
        .map((track) => track.source.prebuffer(this.currentTime))
    );

    this.#rebaseTransport();
    this.isPlaying = true;

    for (const track of this.timeline.tracks) {
      if (track.isLoading || !track.source?.isReady) continue;
      track.source.start(this.currentTime, this.playbackSpeed);
    }

    if (this.onPlayStateChange) {
      this.onPlayStateChange(true);
    }

    this.updatePlayhead();
  }

  /**
   * Stop playback, leaving the playhead where it is.
   */
  stop() {
    if (!this.isPlaying) return;

    for (const track of this.timeline.tracks) {
      track.source?.stop();
    }

    this.isPlaying = false;

    if (this.onPlayStateChange) {
      this.onPlayStateChange(false);
    }

    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  /**
   * Reset playhead to start
   */
  reset() {
    this.stop();
    this.currentTime = 0;
    this.renderer.scheduleRender();

    if (this.onTimeUpdate) {
      this.onTimeUpdate(0);
    }
  }

  /**
   * Seek to specific time
   * @param {number} time - Time in seconds
   */
  seek(time) {
    this.currentTime = Math.max(0, Math.min(this.timeline.duration, time));
    this.#rebaseTransport();

    for (const track of this.timeline.tracks) {
      track.source?.seek(this.currentTime, this.playbackSpeed, this.isPlaying);
    }

    this.renderer.scheduleRender();

    if (this.onTimeUpdate) {
      this.onTimeUpdate(this.currentTime);
    }
  }

  /**
   * Set playback rate.
   *
   * Negative values play backwards and fractional values jog. Streamed tracks
   * support the full range; a locally-decoded file is limited to positive rates,
   * because an AudioBufferSourceNode cannot play a buffer backwards.
   *
   * @param {number} speed - 1 is normal; negative plays in reverse
   */
  setPlaybackSpeed(speed) {
    const clamped = Math.max(-4, Math.min(4, speed));
    if (clamped === this.playbackSpeed) return;

    // Rebase before changing the rate, so the elapsed time so far is measured at
    // the old speed and the new one starts from where the playhead actually is.
    this.#rebaseTransport();
    this.playbackSpeed = clamped;

    for (const track of this.timeline.tracks) {
      track.source?.setSpeed(clamped);
    }

    if (this.onPlaybackSpeedChange) {
      this.onPlaybackSpeedChange(clamped);
    }
  }

  /**
   * Get the current playback rate
   * @returns {number}
   */
  getPlaybackSpeed() {
    return this.playbackSpeed;
  }

  /**
   * Interpolation kernels available to streamed playback.
   *
   * The pump reads at a fractional position, so something decides what the
   * signal is between samples. How much that choice matters depends on how far
   * the rate is being stretched — inaudible on a 48 kHz asset at 1x, very
   * audible on an 8 kHz one.
   *
   * @returns {Array} [{id, label, taps, notes, isDefault}]
   */
  getInterpolators() {
    return interpolatorCatalogue();
  }

  /**
   * Switch the interpolation kernel for every streamed track.
   *
   * Takes effect within a render quantum without interrupting playback, so it
   * can be compared by ear while audio is running.
   *
   * @param {string} id - from `getInterpolators()`
   */
  setInterpolator(id) {
    if (!getInterpolator(id)) {
      const available = interpolatorCatalogue().map((i) => i.id).join(", ");
      throw new Error(`Unknown interpolator "${id}". Available: ${available}`);
    }

    this.interpolatorId = id;
    for (const track of this.timeline.tracks) {
      track.source?.setInterpolator?.(id);
    }

    this.log(`Interpolator: ${id}`);

    if (this.onInterpolatorChange) {
      this.onInterpolatorChange(id);
    }
  }

  /**
   * @returns {string} Current interpolator id
   */
  getInterpolator() {
    return this.interpolatorId;
  }

  /**
   * Hold pitch steady while the playback speed changes.
   *
   * **Proof of concept** — judge it by ear before relying on it. Speech at
   * 0.5x-2x is what it's built for; outside that it warbles. It has no effect at
   * 1x and is bypassed in reverse, and it currently makes its timing decisions
   * per track, so two correlated microphones played together at an off speed
   * will comb-filter against each other.
   *
   * Streamed tracks only: a locally-loaded file plays through an
   * `AudioBufferSourceNode`, which has no such control.
   *
   * @param {boolean} enabled
   */
  setPitchPreservation(enabled) {
    this.preservePitch = !!enabled;

    for (const track of this.timeline.tracks) {
      track.source?.setPitchPreservation?.(this.preservePitch);
    }

    this.log(`Pitch preservation: ${this.preservePitch ? "on" : "off"}`);
    this.onPitchPreservationChange?.(this.preservePitch);
  }

  /**
   * @returns {boolean} Whether pitch preservation is on
   */
  getPitchPreservation() {
    return this.preservePitch;
  }

  /**
   * Speeds between which pitch is held exactly.
   *
   * Outside this range the stretch factor clamps, so the correction becomes
   * *partial* — pitch moves by whatever is left over — rather than the stretcher
   * warbling and doubling transients, which is what an unclamped ratio does at
   * extremes. The clamp is continuous, so jogging across the boundary doesn't
   * click; there is no bypass to switch in and out of.
   *
   * Defaults to 0.5x-2x, which is where WSOLA on speech holds up.
   *
   * @param {number} min - slowest speed at which pitch is still held exactly
   * @param {number} max - fastest
   */
  setPitchPreservationRange(min, max) {
    if (!(min > 0) || !(max > 0) || min > max) {
      throw new Error(`Invalid pitch preservation range ${min}-${max}`);
    }

    this.pitchRange = { min, max };
    for (const track of this.timeline.tracks) {
      track.source?.setPitchPreservationRange?.(min, max);
    }
  }

  /**
   * @returns {{min: number, max: number}} Speeds between which pitch is held
   */
  getPitchPreservationRange() {
    return { ...this.pitchRange };
  }

  /**
   * What pitch preservation is actually doing right now.
   *
   * Worth surfacing rather than inferring, because "on" doesn't mean "holding":
   * it does nothing at 1x by design, and past the configured range it only
   * corrects part of the shift. A host that shows a checkbox and nothing else
   * will have users convinced it's broken at 3x.
   *
   * `alignmentShared` is the honest caveat — see the note on
   * `setPitchPreservation`.
   *
   * @returns {{enabled: boolean, engaged: boolean, pitchRatio: number,
   *   semitones: number, audibleTracks: number, alignmentShared: boolean}}
   */
  getPitchPreservationStatus() {
    const speed = this.playbackSpeed;
    const magnitude = Math.abs(speed);
    const engaged = this.preservePitch && magnitude > 0.02;

    // Mirrors the pump's own clamp, so the two can't describe different things.
    const stretch = engaged
      ? Math.min(1 / this.pitchRange.min, Math.max(1 / this.pitchRange.max, 1 / magnitude))
      : 1;

    // 1.0 means pitch is exactly held; 2.0 is an octave up.
    const pitchRatio = engaged ? stretch * magnitude : magnitude;

    const audibleTracks = this.timeline.tracks.filter(
      (track) => track.canPlay() && this.timeline.isTrackAudible(track)
    ).length;

    return {
      enabled: this.preservePitch,
      engaged,
      pitchRatio,
      semitones: pitchRatio > 0 ? 12 * Math.log2(pitchRatio) : 0,
      audibleTracks,
      // Each pump searches independently, so two correlated microphones played
      // together at an off speed can comb-filter. One track is always exact.
      alignmentShared: !(engaged && audibleTracks > 1),
    };
  }

  /**
   * Whether any track is waiting on audio it hasn't received yet.
   * @returns {boolean}
   */
  isStalled() {
    return this.timeline.tracks.some((track) => track.source?.stalled);
  }

  /**
   * Ranges of each track that could be played right now.
   * @returns {Array} [{trackId, ranges: [{start, end}]}] in track-local seconds
   */
  getBufferedRanges() {
    return this.timeline.tracks.map((track) => ({
      trackId: track.id,
      ranges: track.source ? track.source.getBufferedRanges() : [],
    }));
  }

  /**
   * Set zoom level (pixels per second)
   * @param {number} pixelsPerSecond - Zoom level
   */
  setZoom(pixelsPerSecond) {
    this.timeline.pixelsPerSecond = Math.min(
      500,
      Math.max(0.1, pixelsPerSecond)
    );
    this.timeline.updateViewport(this.renderer.canvas.offsetWidth);

    const visibleDuration = this.timeline.getVisibleDuration();
    const effectiveDuration = this.timeline.getEffectiveDuration(
      this.renderer.canvas.offsetWidth
    );
    const maxScroll = Math.max(0, effectiveDuration - visibleDuration);

    this.timeline.viewportScrollTime = Math.max(
      0,
      Math.min(maxScroll, this.timeline.viewportScrollTime)
    );
    this.timeline.updateViewport(this.renderer.canvas.offsetWidth);

    this.renderer.scheduleRender();

    if (this.onZoomChange) {
      this.onZoomChange(this.timeline.pixelsPerSecond);
    }
  }

  /**
   * Get current zoom level
   * @returns {number} Pixels per second
   */
  getZoom() {
    return this.timeline.pixelsPerSecond;
  }

  /**
   * Zoom to fit all content
   */
  zoomToFit() {
    if (this.timeline.duration === 0) return;

    const containerWidth = this.canvas.offsetWidth;
    const targetZoom = containerWidth / this.timeline.duration;
    this.setZoom(targetZoom);

    this.timeline.viewportScrollTime = 0;
    this.timeline.updateViewport(this.renderer.canvas.offsetWidth);
    this.renderer.scheduleRender();
  }

  /**
   * Scroll the viewport so it starts at a given time.
   *
   * Clamped to the scrollable range. Pairs with `onScrollChange` for driving an
   * external scrollbar.
   * @param {number} time - Time in seconds at the left edge
   */
  setScrollTime(time) {
    this.renderer.scrollToTime(time);
  }

  /**
   * Time at the left edge of the viewport
   * @returns {number} Time in seconds
   */
  getScrollTime() {
    return this.timeline.viewportScrollTime;
  }

  /**
   * What the viewport is currently showing.
   *
   * Everything an external ruler, minimap, or scrollbar needs to draw itself
   * without reaching into the renderer.
   * @returns {Object} {startTime, endTime, duration, pixelsPerSecond, width}
   */
  getViewport() {
    return {
      startTime: this.timeline.viewportStartTime,
      endTime: this.timeline.viewportEndTime,
      duration: this.timeline.getVisibleDuration(),
      pixelsPerSecond: this.timeline.pixelsPerSecond,
      width: this.canvas.offsetWidth,
    };
  }

  /**
   * Scroll the track stack vertically
   * @param {number} offset - Pixels from the top; clamped to the scrollable range
   */
  setVerticalScrollOffset(offset) {
    const max = this.renderer.getMaxVerticalScroll();
    this.renderer.verticalScrollOffset = Math.max(0, Math.min(max, offset));
    this.renderer.scheduleRender();
  }

  /**
   * @returns {number} Current vertical scroll offset in pixels
   */
  getVerticalScrollOffset() {
    return this.renderer.verticalScrollOffset;
  }

  /**
   * @returns {number} Largest useful vertical scroll offset; 0 when everything fits
   */
  getMaxVerticalScrollOffset() {
    return this.renderer.getMaxVerticalScroll();
  }

  /**
   * What the mouse does over the track lanes.
   *
   * The ruler strip, scrollbars, and minimap behave identically either way —
   * only the lanes change:
   *
   * | | `arrange` | `annotate` |
   * |---|---|---|
   * | click a lane | seek | seek |
   * | drag a lane | move that track in time | drag out a region |
   * | cursor | grab | crosshair |
   *
   * `arrange` is the default, so existing hosts are unaffected. Pick `annotate`
   * for anything that isn't aligning tracks against each other — it makes the
   * lanes behave like the ruler, and takes track dragging off the table so a
   * stray drag can't silently move audio in time.
   *
   * Existing regions are still edited from the ruler strip in both modes, since
   * that's where their handles are drawn.
   *
   * @param {string} mode - "arrange" or "annotate"
   */
  setInteractionMode(mode) {
    if (mode !== "arrange" && mode !== "annotate") {
      throw new Error(`Unknown interaction mode "${mode}". Expected "arrange" or "annotate".`);
    }

    this.renderer.interactionMode = mode;
    this.renderer.scheduleRender();
    this.log(`Interaction mode: ${mode}`);

    if (this.onInteractionModeChange) {
      this.onInteractionModeChange(mode);
    }
  }

  /**
   * Keep the transport running when the timeline momentarily empties.
   *
   * Swapping which track is shown means removing one and adding another, and
   * for the instant in between there is nothing loaded. Off, the playhead
   * treats that as the end of the material and stops. On, it rides through, so
   * changing microphone mid-sentence doesn't interrupt playback.
   *
   * A replacement that's shorter still stops playback if the playhead is
   * already past its end — that's genuinely the end of the material, not a gap.
   *
   * @param {boolean} enabled
   */
  setContinuousPlayback(enabled) {
    this.continuousPlayback = !!enabled;
  }

  /**
   * @returns {boolean}
   */
  getContinuousPlayback() {
    return this.continuousPlayback;
  }

  /**
   * @returns {string} "arrange" or "annotate"
   */
  getInteractionMode() {
    return this.renderer.interactionMode;
  }

  /**
   * The interaction modes this build understands.
   * @returns {Array} [{id, label, description}]
   */
  getInteractionModes() {
    return [
      {
        id: "arrange",
        label: "Arrange",
        description: "Drag a track to move it in time. Click to seek.",
      },
      {
        id: "annotate",
        label: "Annotate",
        description: "Click to seek, drag to mark a region. Tracks can't be moved.",
      },
    ];
  }

  /**
   * Whether the viewport follows the playhead during playback
   * @param {boolean} enabled
   */
  setFollowPlayhead(enabled) {
    this.followPlayhead = !!enabled;
  }

  /**
   * @returns {boolean} Whether the viewport follows the playhead
   */
  getFollowPlayhead() {
    return this.followPlayhead;
  }

  /**
   * Whether the transport is running.
   *
   * The same information as the `isPlaying` field and the `onPlayStateChange`
   * callback; here so a host never has to read a field to build a control.
   * @returns {boolean}
   */
  getIsPlaying() {
    return this.isPlaying;
  }

  /**
   * Get current playback time
   * @returns {number} Current time in seconds
   */
  getCurrentTime() {
    return this.currentTime;
  }

  /**
   * Get total duration
   * @returns {number} Duration in seconds
   */
  getDuration() {
    return this.timeline.duration;
  }

  /**
   * Update the visual theme
   * @param {Object} theme - Theme configuration object (partial updates supported)
   */
  setTheme(theme) {
    this.renderer.setTheme(theme);
  }

  /**
   * Get the current theme
   * @returns {Object} Current theme configuration
   */
  getTheme() {
    return { ...this.renderer.theme };
  }

  /**
   * Enable or disable waveform gain scaling
   * When enabled, waveforms visually scale with gain adjustments using soft clipping
   * @param {boolean} enabled - Whether to enable waveform gain scaling
   */
  setWaveformGainScaling(enabled) {
    this.renderer.theme.waveformGainScaling = enabled;
    this.renderer.scheduleRender();
  }

  /**
   * Get whether waveform gain scaling is enabled
   * @returns {boolean} Whether waveform gain scaling is enabled
   */
  getWaveformGainScaling() {
    return this.renderer.theme.waveformGainScaling;
  }

  /**
   * Set selection range
   * @param {number} start - Start time in seconds
   * @param {number} end - End time in seconds
   */
  setSelection(start, end) {
    if (start < 0 || end < 0 || start > end) {
      throw new Error("Invalid selection range");
    }

    this.timeline.selection = {
      start,
      end,
      startSample: this.timeline.timeToSamples(start),
      endSample: this.timeline.timeToSamples(end),
    };
    this.renderer.scheduleRender();

    if (this.onSelectionChange) {
      this.onSelectionChange(this.timeline.selection);
    }
  }

  /**
   * Get current selection
   * @returns {Object|null} Selection object with start and end, or null if no selection
   */
  getSelection() {
    return this.timeline.selection ? { ...this.timeline.selection } : null;
  }

  /**
   * Clear current selection
   */
  clearSelection() {
    this.timeline.selection = null;
    this.renderer.scheduleRender();

    if (this.onSelectionChange) {
      this.onSelectionChange(null);
    }
  }


  // ============ SCALES & LAYOUT ============

  /**
   * Where each drawn track lane sits, in CSS pixels on the main canvas.
   *
   * The library draws a canvas and nothing else, so anything that belongs
   * *beside* a lane — a dB or frequency ruler, a per-track control strip, a
   * label column — is the host's markup and needs the geometry to position
   * itself. This is that geometry, and it accounts for vertical scrolling, the
   * visible-track limit, and track heights recomputed as tracks come and go.
   *
   * Pair it with `onTrackLayoutChange` rather than polling.
   *
   * @returns {Array} [{id, x, y, width, height, visible}] — `visible` is false
   *   for a lane scrolled out of view, which is when a host should hide rather
   *   than position its sidecar elements
   */
  getTrackLayout() {
    return this.renderer.trackLayout();
  }

  /**
   * Attach a dB ruler to a canvas the host owns and positions.
   *
   * The canvas is expected to be exactly as tall as the lane it describes —
   * `getTrackLayout()` gives that height — because the ruler maps its full
   * height to the lane's amplitude range. Centre is amplitude zero, i.e. −∞ dB.
   *
   * **The ruler is a fixed frame of reference.** Per-track gain makes the
   * waveform taller against it rather than moving the ticks, which is what makes
   * a gain change readable at all; a ruler that rescaled itself would leave the
   * waveform looking identical at every setting.
   *
   * @param {string} trackId
   * @param {HTMLCanvasElement} canvas
   * @param {Object} [options]
   * @param {"left"|"right"} [options.tickAlign] - which edge of the ruler canvas
   *   the ticks sit against; overrides `theme.scales.tickAlign`. A ruler to the
   *   left of the waveform wants "right" so its ticks run up against the lane.
   * @param {number} [options.padding] - inset from that edge; 0 makes the ticks
   *   flush against it, which is what puts them right up against the waveform
   * @param {Array<number>} [options.ticks] - dB values, most important first
   * @param {Object} [options.theme] - overrides on top of `theme.scales`
   * @returns {Object} the scale instance, which exposes `measure()`
   */
  attachDbScale(trackId, canvas, options = {}) {
    if (!this.timeline.tracks.some((t) => t.id === trackId)) {
      throw new Error(`Track ${trackId} not found`);
    }
    return this.renderer.attachScale(trackId, "db", canvas, options);
  }

  /**
   * Attach a frequency ruler to a canvas the host owns and positions.
   *
   * Ticks are placed with the same mapping the spectrogram tiles were rendered
   * with — mel by default — so they line up with the picture rather than with a
   * linear axis. The bottom of the scale is the server's analysis floor, 20 Hz
   * in practice; it is not 0 Hz, and the ruler says so rather than rounding it
   * down to a number that was never analysed.
   *
   * Only meaningful for tracks that have a spectrogram.
   *
   * @param {string} trackId
   * @param {HTMLCanvasElement} canvas
   * @param {Object} [options] - as `attachDbScale`
   * @returns {Object} the scale instance
   */
  attachFrequencyScale(trackId, canvas, options = {}) {
    if (!this.timeline.tracks.some((t) => t.id === trackId)) {
      throw new Error(`Track ${trackId} not found`);
    }
    return this.renderer.attachScale(trackId, "frequency", canvas, options);
  }

  /**
   * @param {string} trackId
   * @param {"db"|"frequency"} [kind] - omit to detach both
   */
  detachScale(trackId, kind) {
    if (kind) this.renderer.detachScale(trackId, kind);
    else this.renderer.detachScales(trackId);
  }

  /**
   * Zoom the spectrogram to a frequency band.
   *
   * Affects the drawn spectrogram *and* the frequency ruler together, so they
   * cannot disagree. Done entirely in the browser by cropping the tiles the
   * server already sent — no refetch, no extra endpoint.
   *
   * The resolution cost is smaller than it looks on a mel axis, which is the
   * default: the low end already holds most of the rows, so zooming a 24 kHz
   * asset to 0–4 kHz still has 84 of its 128 bins to stretch.
   *
   * @param {string} trackId
   * @param {number|null} min - Hz, or null to restore the whole analysed range
   * @param {number} [max] - Hz
   */
  setTrackFrequencyRange(trackId, min, max) {
    const track = this.timeline.tracks.find((t) => t.id === trackId);
    if (!track) {
      throw new Error(`Track ${trackId} not found`);
    }

    if (min === null || min === undefined) {
      track.frequencyRange = null;
    } else {
      if (!(max > min)) {
        throw new Error(`Invalid frequency range ${min}-${max}`);
      }
      track.frequencyRange = { min, max };
    }

    this.renderer.drawScales();
    this.renderer.scheduleRender();
    this.onTrackFrequencyRangeChange?.(trackId, track.frequencyRange);
  }

  /**
   * The band currently shown, resolved — never null for a track with a
   * spectrogram, so a host can label an axis without special-casing the default.
   *
   * @param {string} trackId
   * @returns {{min: number, max: number}|null}
   */
  getTrackFrequencyRange(trackId) {
    const track = this.timeline.tracks.find((t) => t.id === trackId);
    if (!track) {
      throw new Error(`Track ${trackId} not found`);
    }
    return this.renderer.frequencyRangeFor(track);
  }

  /**
   * Tint frequency bands — a light overlay where voices sit, say — on every
   * frequency ruler and, unless `setFrequencyHighlightsOnLane(false)`, across
   * the spectrogram lanes too. As many bands as you like; they draw in order,
   * so a later band's colour lands on top where two overlap.
   *
   * DAW-wide: every track, including ones added later, gets its own copy.
   *
   * @param {Array<{min: number, max: number, color: string}>} highlights - Hz,
   *   and any CSS colour; give it alpha (`"rgba(255, 200, 0, 0.15)"`) to keep
   *   the spectrogram readable underneath. Extra fields (an id, a label) are
   *   kept and handed back by `getFrequencyHighlights`.
   */
  setFrequencyHighlights(highlights) {
    if (!Array.isArray(highlights)) {
      throw new Error("Frequency highlights must be an array");
    }
    for (const h of highlights) {
      if (!(Number.isFinite(h?.min) && Number.isFinite(h?.max) && h.max > h.min)) {
        throw new Error(`Invalid frequency highlight range ${h?.min}-${h?.max}`);
      }
      if (typeof h.color !== "string" || !h.color) {
        throw new Error("A frequency highlight needs a color");
      }
    }
    this.frequencyHighlights = highlights.map((h) => ({ ...h }));
    this.#applyFrequencyHighlights();
  }

  /**
   * @returns {Array<{min: number, max: number, color: string}>} a copy
   */
  getFrequencyHighlights() {
    return this.frequencyHighlights.map((h) => ({ ...h }));
  }

  /**
   * Whether highlights also tint the spectrogram lanes, or only the ruler.
   * @param {boolean} enabled - default true
   */
  setFrequencyHighlightsOnLane(enabled) {
    this.frequencyHighlightsOnLane = !!enabled;
    this.#applyFrequencyHighlights();
  }

  /**
   * @returns {boolean}
   */
  getFrequencyHighlightsOnLane() {
    return this.frequencyHighlightsOnLane;
  }

  /**
   * Where a frequency sits in a track's lane, in CSS pixels from the lane top.
   *
   * For crosshairs, readouts and tests. Uses the band currently shown, so it
   * follows a zoom.
   *
   * @param {string} trackId
   * @param {number} hz
   * @returns {number|null} null when the track has no spectrogram
   */
  frequencyToLaneY(trackId, hz) {
    const track = this.timeline.tracks.find((t) => t.id === trackId);
    const range = this.renderer.frequencyRangeFor(track);
    if (!range) return null;

    const fraction = frequencyToFraction(hz, range.min, range.max, track.spectrogram.binScale);
    return this.renderer.calculateTrackHeight() * (1 - fraction);
  }

  /**
   * The inverse — what frequency is at a point in the lane.
   *
   * @param {string} trackId
   * @param {number} y - CSS pixels from the lane top
   * @returns {number|null}
   */
  laneYToFrequency(trackId, y) {
    const track = this.timeline.tracks.find((t) => t.id === trackId);
    const range = this.renderer.frequencyRangeFor(track);
    if (!range) return null;

    const fraction = 1 - y / this.renderer.calculateTrackHeight();
    return fractionToFrequency(fraction, range.min, range.max, track.spectrogram.binScale);
  }

  /**
   * Where a level sits in a track's lane, in CSS pixels from the lane top.
   *
   * @param {number} db - 0 is full scale; `-Infinity` is the centre line
   * @param {number} [sign=1] - +1 for the upper half of the waveform, -1 for the lower
   * @returns {number}
   */
  dbToLaneY(db, sign = 1) {
    return DbScale.yFor(db, this.renderer.calculateTrackHeight(), sign);
  }

  // ============ REGION GROUP MANAGEMENT ============

  /**
   * Create a new region group
   * @param {string} name - Optional name for the region group
   * @param {Object} options - Optional configuration
   * @param {number|null} options.maxRegions - Maximum number of regions (null = unlimited)
   * @param {boolean} options.readOnly - Whether the group is read-only
   * @param {boolean} options.visible - Whether the group draws at all (default: true)
   * @param {boolean} options.interactable - Whether clicking one of its regions can make it
   *   the active group (default: true)
   * @param {string} options.color - CSS colour for this group's regions; auto-assigned from
   *   `theme.regionGroupColors` (cycling) if omitted
   * @param {string} options.textColor - CSS colour for this group's hover readout and name;
   *   falls back to `theme.regionLabelColor` / `theme.regionGroupNameColor`
   * @param {boolean|null} options.showName - force the name on or off for this group; null
   *   (default) follows `setRegionGroupNameDisplay`
   * @returns {string} Group ID
   */
  createRegionGroup(name = "", options = {}) {
    const palette = this.renderer.theme.regionGroupColors;
    const color = options.color || palette[this.timeline.regionGroups.size % palette.length];
    const groupId = this.timeline.createRegionGroup(name, { ...options, color });

    if (this.onRegionGroupsChange) {
      this.onRegionGroupsChange(this.getRegionGroups());
    }

    return groupId;
  }

  /**
   * Set the active region group
   * @param {string} groupId - Group ID to set as active
   */
  setActiveRegionGroup(groupId) {
    if (!this.timeline.regionGroups.has(groupId)) {
      throw new Error(`Region group '${groupId}' does not exist`);
    }
    this.timeline.activeRegionGroupId = groupId;
    this.renderer.scheduleRender();

    if (this.onActiveRegionGroupChange) {
      this.onActiveRegionGroupChange(groupId);
    }
  }

  /**
   * Get the active region group ID
   * @returns {string|null} Active group ID or null if none
   */
  getActiveRegionGroupId() {
    return this.timeline.activeRegionGroupId;
  }

  /**
   * Get all region groups
   * @returns {Array} Array of {id, name, regions, maxRegions, readOnly, visible,
   *   interactable, color, textColor, showName} objects
   */
  getRegionGroups() {
    const groups = [];
    for (const [id, group] of this.timeline.regionGroups.entries()) {
      groups.push({
        id,
        name: group.name,
        regions: group.regions.map((r) => ({ ...r })),
        maxRegions: group.maxRegions,
        readOnly: group.readOnly,
        visible: group.visible !== false,
        interactable: group.interactable !== false,
        color: group.color,
        textColor: group.textColor ?? null,
        showName: group.showName ?? null,
      });
    }
    return groups;
  }

  /**
   * Get regions from a specific group
   * @param {string} groupId - Group ID
   * @returns {Array} Array of {start, end} objects
   */
  getRegions(groupId) {
    const group = this.timeline.regionGroups.get(groupId);
    if (!group) {
      throw new Error(`Region group '${groupId}' does not exist`);
    }
    return group.regions.map((r) => ({ ...r }));
  }

  /**
   * Get regions from the active group
   * @returns {Array} Array of {start, end, startSample, endSample} objects
   */
  getActiveRegions() {
    return this.timeline.getActiveRegions().map((r) => ({ ...r }));
  }

  /**
   * Get the project sample rate (set from first track added)
   * @returns {number|null} Sample rate in Hz, or null if no tracks loaded
   */
  getProjectSampleRate() {
    return this.timeline.projectSampleRate;
  }

  /**
   * Set name for a region group
   * @param {string} groupId - Group ID
   * @param {string} name - New name
   */
  setRegionGroupName(groupId, name) {
    const group = this.timeline.regionGroups.get(groupId);
    if (!group) {
      throw new Error(`Region group '${groupId}' does not exist`);
    }
    group.name = name;

    if (this.onRegionGroupsChange) {
      this.onRegionGroupsChange(this.getRegionGroups());
    }
  }

  /**
   * Remove a region group
   * @param {string} groupId - Group ID to remove
   */
  removeRegionGroup(groupId) {
    if (!this.timeline.regionGroups.has(groupId)) {
      throw new Error(`Region group '${groupId}' does not exist`);
    }

    this.timeline.regionGroups.delete(groupId);

    // If we deleted the active group, clear the active group
    if (this.timeline.activeRegionGroupId === groupId) {
      this.timeline.activeRegionGroupId = null;

      if (this.onActiveRegionGroupChange) {
        this.onActiveRegionGroupChange(null);
      }
    }

    if (this.onRegionGroupsChange) {
      this.onRegionGroupsChange(this.getRegionGroups());
    }

    this.renderer.scheduleRender();
  }

  /**
   * Clear all regions from the active group
   */
  clearActiveRegions() {
    if (!this.timeline.activeRegionGroupId) return;

    const group = this.timeline.regionGroups.get(
      this.timeline.activeRegionGroupId
    );
    if (group) {
      group.regions = [];
      this.renderer.scheduleRender();

      if (this.onRegionsChange) {
        this.onRegionsChange(
          this.timeline.activeRegionGroupId,
          this.getRegions(this.timeline.activeRegionGroupId)
        );
      }
    }
  }

  /**
   * Add a region to the active group
   * @param {number} start - Start time in seconds
   * @param {number} end - End time in seconds
   * @returns {Object|null} The resulting region (may already be merged with a
   *   neighbour), or null if the active group is read-only or full
   */
  addRegion(start, end) {
    if (start < 0 || end < 0 || start > end) {
      throw new Error("Invalid region range");
    }

    const region = this.timeline.addRegionToActiveGroup(start, end);
    this.renderer.scheduleRender();

    if (this.onRegionsChange) {
      this.onRegionsChange(
        this.timeline.activeRegionGroupId,
        this.getRegions(this.timeline.activeRegionGroupId)
      );
    }

    return region;
  }

  /**
   * Set read-only mode for a region group
   * @param {string} groupId - Group ID
   * @param {boolean} readOnly - Whether the group should be read-only
   */
  setRegionGroupReadOnly(groupId, readOnly) {
    const group = this.timeline.regionGroups.get(groupId);
    if (!group) {
      throw new Error(`Region group '${groupId}' does not exist`);
    }
    group.readOnly = readOnly;
    this.renderer.scheduleRender();

    if (this.onRegionGroupsChange) {
      this.onRegionGroupsChange(this.getRegionGroups());
    }
  }

  /**
   * Show or hide a region group. Orthogonal to which group is active — hiding
   * the active group still hides it, it just stays the one new regions and
   * drags apply to.
   * @param {string} groupId - Group ID
   * @param {boolean} visible - Whether the group should be drawn
   */
  setRegionGroupVisible(groupId, visible) {
    const group = this.timeline.regionGroups.get(groupId);
    if (!group) {
      throw new Error(`Region group '${groupId}' does not exist`);
    }
    group.visible = visible;
    this.renderer.scheduleRender();

    if (this.onRegionGroupsChange) {
      this.onRegionGroupsChange(this.getRegionGroups());
    }
  }

  /**
   * Whether clicking one of this group's regions, while some other group is
   * active, switches which group is being edited. Independent of `readOnly`,
   * which governs editing the *active* group rather than switching to one.
   * @param {string} groupId - Group ID
   * @param {boolean} interactable
   */
  setRegionGroupInteractable(groupId, interactable) {
    const group = this.timeline.regionGroups.get(groupId);
    if (!group) {
      throw new Error(`Region group '${groupId}' does not exist`);
    }
    group.interactable = interactable;

    if (this.onRegionGroupsChange) {
      this.onRegionGroupsChange(this.getRegionGroups());
    }
  }

  /**
   * Set the colour a region group's regions draw in.
   * @param {string} groupId - Group ID
   * @param {string} color - Any CSS colour
   */
  setRegionGroupColor(groupId, color) {
    const group = this.timeline.regionGroups.get(groupId);
    if (!group) {
      throw new Error(`Region group '${groupId}' does not exist`);
    }
    group.color = color;
    this.renderer.scheduleRender();

    if (this.onRegionGroupsChange) {
      this.onRegionGroupsChange(this.getRegionGroups());
    }
  }

  /**
   * Set the colour of a region group's text — the start/end/duration readout
   * shown while hovering or dragging, and the group name.
   * @param {string} groupId - Group ID
   * @param {string|null} color - Any CSS colour, or null to fall back to the theme
   */
  setRegionGroupTextColor(groupId, color) {
    const group = this.timeline.regionGroups.get(groupId);
    if (!group) {
      throw new Error(`Region group '${groupId}' does not exist`);
    }
    group.textColor = color || null;
    this.renderer.scheduleRender();

    if (this.onRegionGroupsChange) {
      this.onRegionGroupsChange(this.getRegionGroups());
    }
  }

  /**
   * Force a region group's name on or off, overriding the DAW-wide default.
   * @param {string} groupId - Group ID
   * @param {boolean|null} show - true/false to force; null to follow
   *   `setRegionGroupNameDisplay`
   */
  setRegionGroupShowName(groupId, show) {
    const group = this.timeline.regionGroups.get(groupId);
    if (!group) {
      throw new Error(`Region group '${groupId}' does not exist`);
    }
    group.showName = show === true || show === false ? show : null;
    this.renderer.scheduleRender();

    if (this.onRegionGroupsChange) {
      this.onRegionGroupsChange(this.getRegionGroups());
    }
  }

  /**
   * Default for drawing region-group names on their regions, for any group
   * that hasn't forced its own with `setRegionGroupShowName`. The name sits in
   * the ruler strip level with the drag handles, centred on the visible part
   * of the region, and drops letters as it runs out of room.
   * @param {"always"|"active"|"never"} mode - "active" shows only the group being edited
   */
  setRegionGroupNameDisplay(mode) {
    if (mode !== "always" && mode !== "active" && mode !== "never") {
      throw new Error(
        `Unknown region group name display "${mode}". Expected "always", "active" or "never".`
      );
    }
    this.renderer.regionGroupNameDisplay = mode;
    this.renderer.scheduleRender();
  }

  /**
   * @returns {"always"|"active"|"never"}
   */
  getRegionGroupNameDisplay() {
    return this.renderer.regionGroupNameDisplay;
  }

  /**
   * Set maximum regions for a region group
   * @param {string} groupId - Group ID
   * @param {number|null} maxRegions - Maximum number of regions (null = unlimited)
   */
  setRegionGroupMaxRegions(groupId, maxRegions) {
    const group = this.timeline.regionGroups.get(groupId);
    if (!group) {
      throw new Error(`Region group '${groupId}' does not exist`);
    }
    if (
      maxRegions !== null &&
      (typeof maxRegions !== "number" || maxRegions < 0)
    ) {
      throw new Error("maxRegions must be a positive number or null");
    }
    group.maxRegions = maxRegions;

    if (this.onRegionGroupsChange) {
      this.onRegionGroupsChange(this.getRegionGroups());
    }
  }

  /**
   * Get configuration options for a region group
   * @param {string} groupId - Group ID
   * @returns {Object} Object with {maxRegions, readOnly} properties
   */
  getRegionGroupOptions(groupId) {
    const group = this.timeline.regionGroups.get(groupId);
    if (!group) {
      throw new Error(`Region group '${groupId}' does not exist`);
    }
    return {
      maxRegions: group.maxRegions,
      readOnly: group.readOnly,
      visible: group.visible !== false,
      interactable: group.interactable !== false,
      color: group.color,
      textColor: group.textColor ?? null,
      showName: group.showName ?? null,
    };
  }

  /**
   * How non-active-but-visible region groups draw: "full" boxes them at
   * reduced opacity, "minimized" reduces each to a thin colour bar along the
   * top edge (in both the main lanes and the minimap). The active group is
   * always drawn in full either way.
   * @param {"full"|"minimized"} mode
   */
  setRegionDisplayMode(mode) {
    if (mode !== "full" && mode !== "minimized") {
      throw new Error(`Unknown region display mode "${mode}". Expected "full" or "minimized".`);
    }
    this.renderer.regionDisplayMode = mode;
    this.renderer.scheduleRender();

    if (this.onRegionDisplayModeChange) {
      this.onRegionDisplayModeChange(mode);
    }
  }

  /**
   * @returns {"full"|"minimized"}
   */
  getRegionDisplayMode() {
    return this.renderer.regionDisplayMode;
  }

  // ============ INTERNAL METHODS ============

  /**
   * Every track comes through here, so DAW-wide settings that each track keeps
   * its own copy of are seeded in one place.
   */
  #createTrack(id) {
    const track = new Track(id);
    track.frequencyHighlights = this.frequencyHighlights.map((h) => ({ ...h }));
    track.frequencyHighlightsOnLane = this.frequencyHighlightsOnLane;
    return track;
  }

  /** Push the DAW-wide highlight setting into every track's copy, then redraw. */
  #applyFrequencyHighlights() {
    for (const track of this.timeline.tracks) {
      track.frequencyHighlights = this.frequencyHighlights.map((h) => ({ ...h }));
      track.frequencyHighlightsOnLane = this.frequencyHighlightsOnLane;
    }
    this.renderer.drawScales();
    this.renderer.scheduleRender();
    this.onFrequencyHighlightsChange?.(
      this.getFrequencyHighlights(),
      this.frequencyHighlightsOnLane
    );
  }

  async initAudioContext() {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext ||
        window.webkitAudioContext)();
    }
  }

  /**
   * Build a track's audio chain:
   * source -> preGain (dB) -> [compressor] -> gain (volume) -> pan -> destination
   *
   * Shared by local and remote tracks — a streamed track needs exactly the same
   * downstream chain, only fed by the worklet instead of a buffer.
   * @private
   */
  #buildTrackChain(track) {
    const preGainNode = this.audioContext.createGain();
    preGainNode.gain.value = Math.pow(10, track.gain / 20);

    let compressorNode = null;
    if (track.useCompressor) {
      compressorNode = this.audioContext.createDynamicsCompressor();
      // Settings optimized for speech
      compressorNode.threshold.value = -24;
      compressorNode.knee.value = 30;
      compressorNode.ratio.value = 12;
      compressorNode.attack.value = 0.003;
      compressorNode.release.value = 0.25;
    }

    const limiterNode = this.audioContext.createWaveShaper();
    limiterNode.curve = LIMITER_CURVE;
    // oversample deliberately left "none". WaveShaperNode is specified to clamp
    // an input outside [-1, 1] to the curve's edge value, and that holds with
    // no oversampling — measured bit-exact even at +40dB gain on a hot signal.
    // With "4x" (or "2x"), Chromium's oversampling breaks that guarantee for a
    // hard-driven signal: the same +40dB case measured a peak 44% *past* full
    // scale, which is exactly the failure this node exists to prevent. Some
    // extra harmonic content from the unoversampled lookup is a fine trade for
    // a safety ceiling that's actually a ceiling.

    const gainNode = this.audioContext.createGain();
    gainNode.gain.value = this.#audibleVolume(track);

    const panNode = this.audioContext.createStereoPanner();
    panNode.pan.value = track.pan;

    let currentNode = preGainNode;
    if (compressorNode) {
      preGainNode.connect(compressorNode);
      currentNode = compressorNode;
    }
    currentNode.connect(limiterNode);
    limiterNode.connect(gainNode);
    gainNode.connect(panNode);
    panNode.connect(this.audioContext.destination);

    track.preGainNode = preGainNode;
    track.compressorNode = compressorNode;
    track.limiterNode = limiterNode;
    track.gainNode = gainNode;
    track.panNode = panNode;
  }

  /**
   * Anchor the transport to the audio clock at the current playhead.
   *
   * Everything downstream derives position from this, so every track advances
   * off one clock at one rate. That's what keeps them aligned without any
   * correction: the pump's increment is `speed × fileRate / contextRate` per
   * output sample, which is exactly the transport's rate by construction.
   * @private
   */
  #rebaseTransport() {
    this.transportAnchorTime = this.currentTime;
    this.transportAnchorContextTime = this.audioContext ? this.audioContext.currentTime : 0;
  }

  /** Transport position implied by the audio clock right now. @private */
  #transportTime() {
    if (!this.audioContext) return this.currentTime;
    const elapsed = this.audioContext.currentTime - this.transportAnchorContextTime;
    return this.transportAnchorTime + elapsed * this.playbackSpeed;
  }

  updatePlayhead() {
    if (!this.isPlaying) return;

    // A stalled source holds its position, so let the playhead hold too rather
    // than run ahead of anything audible.
    if (!this.isStalled()) {
      this.currentTime = this.#transportTime();
    } else {
      this.#rebaseTransport();
    }

    if (this.onTimeUpdate) {
      this.onTimeUpdate(this.currentTime);
    }

    // Auto-scroll to follow playhead if enabled
    if (this.followPlayhead && this.renderer && this.timeline) {
      const playheadX = this.timeline.timeToCanvasX(this.currentTime);
      const canvasWidth = this.renderer.canvas.offsetWidth;
      const centerX = canvasWidth / 2;

      const runningBackwards = this.playbackSpeed < 0;
      if ((!runningBackwards && playheadX >= centerX) || (runningBackwards && playheadX <= centerX)) {
        const visibleDuration = this.timeline.getVisibleDuration();
        this.renderer.scrollToTime(this.currentTime - visibleDuration / 2);
      }
    }

    this.renderer.scheduleRender();

    // An empty timeline is the end of the material — unless we're mid-swap, in
    // which case it's a gap and the transport should ride through it.
    const isGap = this.continuousPlayback && this.timeline.tracks.length === 0;

    const runOff =
      !isGap &&
      (this.playbackSpeed >= 0
        ? this.currentTime >= this.timeline.duration
        : this.currentTime <= 0);

    if (runOff) {
      this.currentTime = this.playbackSpeed >= 0 ? this.timeline.duration : 0;
      this.stop();
    } else {
      this.animationFrameId = requestAnimationFrame(() => this.updatePlayhead());
    }
  }

  recalculateDuration() {
    let maxDuration = 0;
    for (const track of this.timeline.tracks) {
      if (track.hasAudio()) {
        const trackEnd = track.timeOffset + track.duration;
        if (trackEnd > maxDuration) {
          maxDuration = trackEnd;
        }
      }
    }
    const oldDuration = this.timeline.duration;
    this.timeline.duration = maxDuration;

    if (oldDuration !== maxDuration && this.onDurationChange) {
      this.onDurationChange(maxDuration);
    }
  }

  // ============ STATE MANAGEMENT ============

  /**
   * Get the current state of the DAW for serialization
   * Note: Audio buffers are not serialized - only metadata and settings
   * @returns {Object} Serializable state object
   */
  getState() {
    return {
      version: "1.0.0",
      timeline: {
        pixelsPerSecond: this.timeline.pixelsPerSecond,
        duration: this.timeline.duration,
        projectSampleRate: this.timeline.projectSampleRate,
        tracks: this.timeline.tracks.map((track) => ({
          id: track.id,
          fileName: track.fileName,
          timeOffset: track.timeOffset,
          sampleOffset: track.sampleOffset,
          originalSampleRate: track.originalSampleRate,
          volume: track.volume,
          gain: track.gain,
          pan: track.pan,
          useCompressor: track.useCompressor,
          muted: track.muted,
          soloed: track.soloed,
          color: track.color,
          spectrogramOpacity: track.spectrogramOpacity,
          spectrogramVariant: track.spectrogram?.variantId ?? null,
          // Note: audioBuffer is not serialized - must be reloaded
        })),
      },
      regionGroups: Array.from(this.timeline.regionGroups.entries()).map(
        ([id, group]) => ({
          id,
          name: group.name,
          regions: group.regions.map((r) => ({
            id: r.id,
            start: r.start,
            end: r.end,
            startSample: r.startSample,
            endSample: r.endSample,
          })),
          maxRegions: group.maxRegions,
          readOnly: group.readOnly,
          visible: group.visible !== false,
          interactable: group.interactable !== false,
          color: group.color,
          textColor: group.textColor ?? null,
          showName: group.showName ?? null,
        })
      ),
      activeRegionGroupId: this.timeline.activeRegionGroupId,
      playback: {
        currentTime: this.currentTime,
        isPlaying: this.isPlaying,
        followPlayhead: this.followPlayhead,
      },
      view: {
        interactionMode: this.renderer ? this.renderer.interactionMode : "arrange",
        regionDisplayMode: this.renderer ? this.renderer.regionDisplayMode : "full",
        regionGroupNameDisplay: this.renderer ? this.renderer.regionGroupNameDisplay : "never",
        frequencyHighlights: this.getFrequencyHighlights(),
        frequencyHighlightsOnLane: this.frequencyHighlightsOnLane,
        scrollTime: this.renderer ? this.renderer.timeline.viewportScrollTime : 0,
        verticalScrollOffset: this.renderer
          ? this.renderer.verticalScrollOffset
          : 0,
      },
      theme: this.renderer ? this.renderer.theme : null,
      settings: {
        enableLogging: this.enableLogging,
      },
    };
  }

  /**
   * Restore DAW state from a serialized state object
   * Note: Audio files must be provided separately and matched by track ID
   * @param {Object} state - State object from getState()
   * @param {Object} audioFiles - Map of track IDs to audio File/ArrayBuffer objects
   * @returns {Promise<void>}
   */
  async setState(state, audioFiles = {}) {
    if (!state || state.version !== "1.0.0") {
      throw new Error("Invalid or unsupported state version");
    }

    // Stop playback
    this.stop();

    // Clear existing tracks
    const existingTrackIds = [...this.timeline.tracks.map((t) => t.id)];
    for (const trackId of existingTrackIds) {
      this.removeTrack(trackId);
    }

    // Restore timeline settings
    this.timeline.pixelsPerSecond = state.timeline.pixelsPerSecond;
    if (state.timeline.projectSampleRate) {
      this.timeline.projectSampleRate = state.timeline.projectSampleRate;
    }
    if (this.renderer) {
      this.renderer.timeline.pixelsPerSecond = state.timeline.pixelsPerSecond;
    }

    // Restore tracks
    for (const trackState of state.timeline.tracks) {
      const audioData = audioFiles[trackState.id];

      if (audioData) {
        // Add track with audio data
        await this.addTrack(audioData, {
          id: trackState.id,
          fileName: trackState.fileName,
          timeOffset: trackState.timeOffset,
          volume: trackState.volume,
          color: trackState.color,
        });

        // Apply additional settings
        this.setTrackGain(trackState.id, trackState.gain);
        this.setTrackPan(trackState.id, trackState.pan);
        this.setTrackCompressor(trackState.id, trackState.useCompressor);
        // Older saved states predate these, so default rather than undefine.
        this.setTrackMuted(trackState.id, trackState.muted ?? false);
        this.setTrackSolo(trackState.id, trackState.soloed ?? false);

        // Restore sample offset and original sample rate if available
        const track = this.timeline.tracks.find((t) => t.id === trackState.id);
        if (track) {
          if (trackState.originalSampleRate !== undefined) {
            track.originalSampleRate = trackState.originalSampleRate;
          }
          if (trackState.sampleOffset !== undefined) {
            track.setSampleOffset(trackState.sampleOffset);
          }
          if (trackState.spectrogramOpacity !== undefined) {
            track.spectrogramOpacity = trackState.spectrogramOpacity;
          }
          if (trackState.spectrogramVariant && track.spectrogram) {
            track.spectrogram.setVariant(trackState.spectrogramVariant);
          }
        }
      } else {
        // Create placeholder track without audio (will show loading state)
        const track = this.#createTrack(trackState.id);
        track.fileName = trackState.fileName;
        track.timeOffset = trackState.timeOffset;
        track.sampleOffset = trackState.sampleOffset || 0;
        track.originalSampleRate = trackState.originalSampleRate || null;
        track.spectrogramOpacity = trackState.spectrogramOpacity || 0;
        track.volume = trackState.volume;
        track.gain = trackState.gain;
        track.pan = trackState.pan;
        track.useCompressor = trackState.useCompressor;
        track.muted = trackState.muted ?? false;
        track.soloed = trackState.soloed ?? false;
        track.color = trackState.color;
        track.isLoading = true;
        track.loadingPhase = "audio";

        this.timeline.tracks.push(track);
        this.log(
          `Track created as placeholder: ${trackState.id} (${trackState.fileName}) - waiting for audio data`
        );
      }
    }

    // Clear existing region groups
    for (const groupId of [...this.timeline.regionGroups.keys()]) {
      this.removeRegionGroup(groupId);
    }

    // Restore region groups, preserving their original IDs so that
    // activeRegionGroupId and any IDs the host is holding still resolve.
    let highestRestoredIndex = -1;
    let highestRestoredRegionIndex = -1;
    for (const groupState of state.regionGroups) {
      this.timeline.regionGroups.set(groupState.id, {
        name: groupState.name,
        regions: groupState.regions.map((r) => {
          // Older saved states predate stable region ids — backfill rather
          // than leave them undefined, so lookups by id keep working.
          const id = r.id || `region-${this.timeline.regionIdCounter++}`;
          const generated = /^region-(\d+)$/.exec(id);
          if (generated) {
            highestRestoredRegionIndex = Math.max(highestRestoredRegionIndex, Number(generated[1]));
          }
          return { id, start: r.start, end: r.end, startSample: r.startSample, endSample: r.endSample };
        }),
        maxRegions: groupState.maxRegions,
        readOnly: groupState.readOnly,
        // Also predate visible/interactable/color — default to the same
        // "fully shown and editable" state a fresh group would have.
        visible: groupState.visible !== undefined ? groupState.visible : true,
        interactable: groupState.interactable !== undefined ? groupState.interactable : true,
        color: groupState.color || null,
        textColor: groupState.textColor || null,
        showName:
          groupState.showName === true || groupState.showName === false
            ? groupState.showName
            : null,
      });

      const generated = /^region-group-(\d+)$/.exec(groupState.id);
      if (generated) {
        highestRestoredIndex = Math.max(highestRestoredIndex, Number(generated[1]));
      }
    }

    // Keep the ID counters ahead of what was restored, so anything created
    // after this doesn't collide with one of them.
    this.timeline.regionGroupIdCounter = Math.max(
      this.timeline.regionGroupIdCounter,
      highestRestoredIndex + 1
    );
    this.timeline.regionIdCounter = Math.max(
      this.timeline.regionIdCounter,
      highestRestoredRegionIndex + 1
    );

    if (this.onRegionGroupsChange) {
      this.onRegionGroupsChange(this.getRegionGroups());
    }

    // Restore active region group
    if (state.activeRegionGroupId) {
      this.setActiveRegionGroup(state.activeRegionGroupId);
    }

    // Restore playback state
    this.followPlayhead = state.playback.followPlayhead;
    if (state.playback.currentTime > 0) {
      this.seek(state.playback.currentTime);
    }

    // Restore view state
    if (this.renderer && state.view) {
      if (state.view.scrollTime !== undefined) {
        this.renderer.scrollToTime(state.view.scrollTime);
      }
      if (state.view.verticalScrollOffset !== undefined) {
        this.renderer.verticalScrollOffset = state.view.verticalScrollOffset;
      }
      if (state.view.interactionMode) {
        this.setInteractionMode(state.view.interactionMode);
      }
      if (state.view.regionDisplayMode) {
        this.setRegionDisplayMode(state.view.regionDisplayMode);
      }
      if (state.view.regionGroupNameDisplay) {
        this.setRegionGroupNameDisplay(state.view.regionGroupNameDisplay);
      }
      if (state.view.frequencyHighlights) {
        this.setFrequencyHighlights(state.view.frequencyHighlights);
      }
      if (state.view.frequencyHighlightsOnLane !== undefined) {
        this.setFrequencyHighlightsOnLane(state.view.frequencyHighlightsOnLane);
      }
    }

    // Restore theme
    if (state.theme) {
      this.setTheme(state.theme);
    }

    // Restore settings
    if (state.settings) {
      this.setLoggingEnabled(state.settings.enableLogging);
    }

    // Recalculate duration
    this.recalculateDuration();

    // Render
    if (this.renderer) {
      this.renderer.scheduleRender();
    }

    this.log("State restored successfully");
  }

  // ============ LIFECYCLE ============

  /**
   * Clean up all resources and event listeners
   * Call this when unmounting/removing the DAW instance
   */
  destroy() {
    this.log("Destroying AudioDAW instance");

    // Every attached ruler holds a ResizeObserver on a host-owned canvas, which
    // would otherwise outlive the DAW that feeds it.
    for (const trackId of [...this.renderer.scales.keys()]) {
      this.renderer.detachScales(trackId);
    }

    // Stop playback and cancel animation frames
    this.stop();
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    // Tear down playback sources
    for (const track of this.timeline.tracks) {
      track.source?.destroy();
      track.source = null;
    }

    // Disconnect all audio nodes and stop in-flight peak fetches
    for (const track of this.timeline.tracks) {
      if (track.waveform) {
        track.waveform.destroy();
        track.waveform = null;
      }
      if (track.spectrogram) {
        track.spectrogram.destroy();
        track.spectrogram = null;
      }
      if (track.gainNode) {
        track.gainNode.disconnect();
        track.gainNode = null;
      }
      if (track.preGainNode) {
        track.preGainNode.disconnect();
        track.preGainNode = null;
      }
      if (track.panNode) {
        track.panNode.disconnect();
        track.panNode = null;
      }
      if (track.compressorNode) {
        track.compressorNode.disconnect();
        track.compressorNode = null;
      }
      if (track.limiterNode) {
        track.limiterNode.disconnect();
        track.limiterNode = null;
      }
    }

    // Close audio context
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }

    // Clean up renderer if it has a destroy method
    if (this.renderer && typeof this.renderer.destroy === "function") {
      this.renderer.destroy();
    }

    // Clean up context menu
    if (this.renderer && this.renderer.hideContextMenu) {
      this.renderer.hideContextMenu();
    }

    // Clear references
    this.timeline = null;
    this.renderer = null;
    this.canvas = null;
    this.minimapCanvas = null;

    // Clear callbacks
    this.onLoadingStateChange = null;
    this.onPlayStateChange = null;
    this.onTimeUpdate = null;
    this.onSelectionChange = null;
    this.onRegionsChange = null;
    this.onRegionsDrag = null;
    this.onFrequencyHighlightsChange = null;
    this.onTrackAdded = null;
    this.onTrackRemoved = null;
    this.onTrackOffsetChange = null;
    this.onTrackVolumeChange = null;
    this.onTrackPanChange = null;
    this.onTrackGainChange = null;
    this.onTrackCompressorChange = null;
    this.onTrackColorChange = null;
    this.onTrackSpectrogramOpacityChange = null;
    this.onTrackSpectrogramVariantChange = null;
    this.onPlaybackSpeedChange = null;
    this.onInterpolatorChange = null;
    this.onPitchPreservationChange = null;
    this.onTrackMuteChange = null;
    this.onTrackSoloChange = null;
    this.onTrackLayoutChange = null;
    this.onTrackFrequencyRangeChange = null;
    this.onInteractionModeChange = null;
    this.onTrackReplaced = null;
    this.onZoomChange = null;
    this.onDurationChange = null;
    this.onScrollChange = null;
    this.onActiveRegionGroupChange = null;
    this.onRegionGroupsChange = null;
    this.onRegionDisplayModeChange = null;

    this.log("AudioDAW instance destroyed");
  }
}
